import React, {useEffect, createContext, useCallback, useContext, useMemo, useState, useRef } from 'react';
import { useNodesState, useEdgesState, addEdge } from 'reactflow';
import Modal from 'react-bootstrap/Modal';
import {
  getSubscription,
  getSchema,
  getMarketplaceEndpoints,
  getMapping,
  saveMapping,
  getShippingMappings,
  saveShippingMappings,
  getPaymentMappings,
  savePaymentMappings,
  getSalesPersonMappings,
  saveSalesPersonMappings,
  getShippingOptions,
  getPaymentOptions,
  getSalesPersonOptions
} from '../fieldMapperApi';

import SuccessPopup from './SuccessPopup';
import { useUnsavedChanges } from '../UnsavedChangesGuard';
import { generateSkeletonTree } from './utils/skeletonTreeGenerator';
import { EDGE_COLOR } from './edgeColors';
import Style from './GraphContext.module.css';


// Enhanced autoPositionNodes function for proper tree layout with controlled “garland” positioning for leaf nodes
const autoPositionNodes = (nodes) => {
  const positions = {};
  const spacing = {
    horizontalGap: 240,
    verticalGap: 100, // Slightly increased for more space between nodes
    leftPadding: 100,
    topPadding: 50
  };

  const customerNode = nodes.find(node => node.id === 'customer');
  const clientNode = nodes.find(node => node.id === 'client');

  let customerY = spacing.topPadding + 100;

  // Helper to track used Y positions per column to avoid overlap
  const usedY = {};

  // Place the Customer node in the middle vertically on the left side
  if (customerNode) {
    const customerChildren = nodes.filter(node => node.data.parentId === 'customer');
    const totalHeight = customerChildren.length * spacing.verticalGap;
    customerY = spacing.topPadding + totalHeight / 2;

    // Store original customer position for children positioning
    const originalCustomerX = spacing.leftPadding - 800;
    
    // Position customer node at the desired location (moved left)
    positions[customerNode.id] = { x: spacing.leftPadding - 1000, y: customerY };

    // Recursively position all descendants of customer using original position
    const positionDescendants = (parentId, parentX, parentY, depth = 1) => {
      const children = nodes.filter(node => node.data.parentId === parentId);
      if (!children.length) return;
      // Calculate yStart so that children are centered under parent
      const totalHeight = children.length * spacing.verticalGap;
      let yStart = parentY - totalHeight / 2;
      if (yStart < spacing.topPadding) yStart = spacing.topPadding;
      children.forEach((child) => {
        const x = parentX + spacing.horizontalGap;
        if (!usedY[x]) usedY[x] = yStart;
        const y = usedY[x];
        positions[child.id] = { x, y };
        usedY[x] += spacing.verticalGap;
        positionDescendants(child.id, x, y, depth + 1);
      });
    };
    // Use original customer position for positioning children, not the moved position
    positionDescendants('customer', originalCustomerX, customerY, 1);
  }

  // Place Client node and recursively position all descendants
  if (clientNode) {
    positions[clientNode.id] = { x: spacing.leftPadding + 720, y: customerY };

    const positionDescendants = (parentId, parentX, parentY, depth = 1) => {
      const children = nodes.filter(node => node.data.parentId === parentId);
      if (!children.length) return;
      const totalHeight = children.length * spacing.verticalGap;
      let yStart = parentY - totalHeight / 2;
      if (yStart < spacing.topPadding) yStart = spacing.topPadding;
      children.forEach((child) => {
        const x = parentX - spacing.horizontalGap;
        if (!usedY[x]) usedY[x] = yStart;
        const y = usedY[x];
        positions[child.id] = { x, y };
        usedY[x] += spacing.verticalGap;
        positionDescendants(child.id, x, y, depth + 1);
      });
    };
    positionDescendants('client', positions[clientNode.id].x, positions[clientNode.id].y, 1);
  }

  return nodes.map(node => ({
    ...node,
    position: positions[node.id] || node.position
  }));
};
/**
 * Function to calculate node levels and identify node types recursively.
 * This supports unlimited hierarchy depth.
 */
const inferNodeTypesMultilevel = (nodes) => {
  const hierarchy = {
    childrenByParent: {},
    hasChildrenMap: {},
    roots: [],
    nodeTypes: {}
  };
  
  // First pass: build the parent-child relationships
  nodes.forEach(node => {
    if (!node.data.parentId) {
      hierarchy.roots.push(node.id);
    } else {
      if (!hierarchy.childrenByParent[node.data.parentId]) {
        hierarchy.childrenByParent[node.data.parentId] = [];
      }
      hierarchy.childrenByParent[node.data.parentId].push(node.id);
    }
  });
  
  // Second pass: identify which nodes have children (recursively)
  const determineHasChildren = (nodeId) => {
    const children = hierarchy.childrenByParent[nodeId] || [];
    const hasChildren = children.length > 0;
    
    hierarchy.hasChildrenMap[nodeId] = hasChildren;
    
    // Recursively process children
    children.forEach(childId => determineHasChildren(childId));
  };
  
  // Process all roots
  hierarchy.roots.forEach(rootId => determineHasChildren(rootId));
  
  // Third pass: determine node types
  nodes.forEach(node => {
    const isRoot = hierarchy.roots.includes(node.id);
    const hasChildren = hierarchy.hasChildrenMap[node.id] || false;
    const isLeaf = !hasChildren;
    
    hierarchy.nodeTypes[node.id] = {
      isRootNode: isRoot,
      isParent: hasChildren,
      hasChildren,
      isLeaf
    };
  });
  
  // Apply node types to nodes
  return nodes.map(node => ({
    ...node,
    type: 'universal',
    data: {
      ...node.data,
      ...hierarchy.nodeTypes[node.id]
    }
  }));
};

// Initial state with only root nodes (the rest will be loaded from API)
// Removed the old initialNodes and processedNodes from the top-level scope

// Apply multilevel type inference and auto positioning
// Removed the old processedNodes from the top-level scope

// Initial field mapping edges (empty as they'll be created from API data)
const initialFieldEdges = [
  /* Commented out static edges as they'll be created from API data
  {
    id: 'e1-2',
    source: 'item-no',
    target: 'product-sku',
    sourceHandle: 'r',
    targetHandle: 'l',
    type: 'custom',
    data: { 
      label: 'Item No → SKU', 
      transformationType: 'none',
      hasTransformation: false 
    }
  },
  {
    id: 'e1-3',
    source: 'item-desc',
    target: 'product-name',
    sourceHandle: 'r',
    targetHandle: 'l',
    type: 'custom',
    data: { 
      label: 'Description → Name', 
      transformationType: 'trim',
      hasTransformation: true,
      options: { trim: true }
    }
  },
  {
    id: 'e1-4',
    source: 'item-price',
    target: 'product-price',
    sourceHandle: 'r',
    targetHandle: 'l',
    type: 'custom',
    data: { 
      label: 'Unit Price → Price', 
      transformationType: 'formatNumber',
      hasTransformation: true,
      options: { precision: 2 }
    }
  }
  */
];

const DEFAULT_EDGE_STYLE = { stroke: EDGE_COLOR, strokeWidth: 2 };

const GraphContext = createContext({
  nodes: [],
  edges: [],
  onNodesChange: () => {},
  onEdgesChange: () => {},
  handleConnect: () => {},
  updateEdgeLabel: () => {},
  setEdgeTransformation: () => {},
  selectNode: () => {},
  removeEdge: () => {},
  applySuggestion: () => {},
  saveState: () => {},
  handleNodeCollapse: () => {},
  isValidConnection: () => true,
  suggestChildMappings: () => {},
  validateTargetPath: (path) => path,
  isLoadingSchema: false,
  schemaError: null,
  setSchemaError: () => {},
  message: null,
  setMessage: () => {},
  isErpToEcomm: true,
  toggleDirection: () => {},
  isSkeletonLoading: false,
  showSkeletonTree: () => {},
  hideSkeletonTree: () => {}
});

// Function to automatically generate tree edges based on parent-child relationships
const generateTreeEdges = (nodes) => {
  const treeEdges = [];
  
  // Find all source vs target nodes - assume nodes with parentId from first root
  // are source nodes, and nodes with parentId from second root are target nodes
  const roots = nodes.filter(node => !node.data.parentId).map(node => node.id);
  const sourceRoot = roots[0];
  
  // For each node with a parentId, create an edge from parent to child
  nodes.forEach(node => {
    if (node.data.parentId) {
      const parentNode = nodes.find(n => n.data.parentId === node.data.parentId);
      if (parentNode) {
        // Determine if this is in the source or target tree
        const parentHierarchy = [];
        let currentId = node.data.parentId;
        while (currentId) {
          parentHierarchy.push(currentId);
          const lookupId = currentId;
          const currentNode = nodes.find(n => n.id === lookupId);
          currentId = currentNode?.data.parentId;
        }
        
        // If root ancestor is the first root, it's a source tree node
        const isSourceTree = parentHierarchy.includes(sourceRoot);
        
        // Determine the appropriate handles based on node types
        const parentNode = nodes.find(n => n.id === node.data.parentId);
        const isParentRoot = parentNode && !parentNode.data.parentId;
        
        // For root nodes, use bottom handle as source
        // For other nodes, use right handle for source tree, left handle for target tree
        const sourceHandle = isParentRoot ? 'b' : isSourceTree ? 'r' : 'b';
        
        // For target handle, use top handle for all child nodes
        const targetHandle = 't';
        
        treeEdges.push({
          id: `tree-edge-${node.data.parentId}-${node.id}`,
          source: node.data.parentId,
          target: node.id,
          sourceHandle: sourceHandle,
          targetHandle: targetHandle,
          type: 'tree',
          animated: false,
          style: { 
            strokeWidth: 2,
            opacity: 0.6
          }
        });
      }
    }
  });
  
  return treeEdges;
};

// ── Virtual entities (Payment, Shipping, Sales Person) ────────────────────────
// They have no schema. Their nodes are the values used by saved/added mappings, and every
// mapping is drawn as an edge between its two value nodes. The values each system offers
// (the *-methods / salesPersonCode endpoints) are not drawn; they fill the Add Custom
// Attributes dropdowns and give the nodes their display labels.
const VIRTUAL_CONFIG = {
  shipping: { prefix: 'shipping', description: 'Shipping method', erpKey: 'erpMethod', ecomKey: 'ecommerceMethod' },
  payment: { prefix: 'payment', description: 'Payment method', erpKey: 'erpMethod', ecomKey: 'ecommerceMethod' },
  'sales person': { prefix: 'salesperson', description: 'Sales person', erpKey: 'erpSalesPersonCode', ecomKey: 'ecommerceSalesPersonCode' },
};
const EMPTY_VIRTUAL_OPTIONS = { erp: [], ecom: [] };
const EMPTY_LIST = [];
const EMPTY_SET = new Set();

const toValue = (value) => (value === null || value === undefined ? '' : String(value));
const sanitizeIdPart = (s) => (s ? String(s).replace(/[^a-zA-Z0-9]/g, '_') : 'unknown');

// One leaf node per distinct mapped value on each side; idByValue maps each side's value to its node id.
const buildVirtualGraph = (config, options, mappings, isErpToEcomm, cleanLabel) => {
  const idByValue = { erp: new Map(), ecom: new Map() };
  const nodes = [];
  if (!config) return { nodes, idByValue };
  const sides = [
    { side: 'erp', key: config.erpKey, parentId: isErpToEcomm ? 'customer' : 'client', methodType: isErpToEcomm ? 'erp' : 'ecommerce' },
    { side: 'ecom', key: config.ecomKey, parentId: isErpToEcomm ? 'client' : 'customer', methodType: isErpToEcomm ? 'ecommerce' : 'erp' },
  ];
  sides.forEach(({ side, key, parentId, methodType }) => {
    const optionLabels = new Map((options[side] || []).map(option => [toValue(option.value), option.label]));
    const labels = new Map();
    (mappings || []).forEach(mapping => {
      const value = toValue(mapping?.[key]);
      if (value && !labels.has(value)) labels.set(value, optionLabels.get(value) || cleanLabel(value));
    });
    [...labels.entries()].forEach(([value, label], index) => {
      const id = `${config.prefix}-${side}-${sanitizeIdPart(value)}-${index}`;
      idByValue[side].set(value, id);
      nodes.push({
        id,
        data: {
          label,
          parentId,
          isMethod: true,
          methodType,
          fieldType: 'string',
          isRequired: false,
          description: config.description,
          isCustom: true,
          virtualSide: side,
          virtualValue: value,
        },
        position: { x: 0, y: 0 },
      });
    });
  });
  return { nodes, idByValue };
};

// Edge per saved/added mapping, skipping the ones the user removed. Ids end in the mapping
// index so removeEdge can record which mapping was removed.
// savedKeys holds the pairs last loaded from / saved to the backend (null until loaded); an edge for any
// other pair is drawn but not saved yet.
const virtualPairKey = (config, mapping) => `${toValue(mapping?.[config.erpKey])}|${toValue(mapping?.[config.ecomKey])}`;
const toVirtualPairKeys = (config, mappings) => new Set((mappings || []).map(mapping => virtualPairKey(config, mapping)));

const buildVirtualAutoEdges = (config, mappings, removedIndices, idByValue, isErpToEcomm, savedKeys) => (mappings || [])
  .map((mapping, index) => {
    if (removedIndices.has(index)) return null; // skip removed
    const erpValue = toValue(mapping?.[config.erpKey]);
    const ecomValue = toValue(mapping?.[config.ecomKey]);
    const erpNodeId = idByValue.erp.get(erpValue);
    const ecomNodeId = idByValue.ecom.get(ecomValue);
    if (!erpNodeId || !ecomNodeId) return null;
    return {
      id: `${config.prefix}-auto-edge-${index}`,
      source: isErpToEcomm ? erpNodeId : ecomNodeId,
      target: isErpToEcomm ? ecomNodeId : erpNodeId,
      sourceHandle: 'r',
      targetHandle: 'l',
      type: 'custom',
      animated: true,
      style: { stroke: EDGE_COLOR, strokeWidth: 2 },
      data: {
        label: `${erpValue} -> ${ecomValue}`,
        transformationType: 'none',
        hasTransformation: false,
        unsaved: Boolean(savedKeys) && !savedKeys.has(virtualPairKey(config, mapping)),
      },
    };
  })
  .filter(Boolean);

// The { erp, ecom } pairs currently on the canvas: auto edges plus edges the user
// connected by hand between an ERP and an eCommerce value node. Each pair appears once.
const getVirtualPairsFromEdges = (edges, virtualNodes) => {
  const dataById = new Map(virtualNodes.map(node => [node.id, node.data]));
  const seen = new Set();
  return edges.reduce((pairs, edge) => {
    const source = dataById.get(edge.source);
    const target = dataById.get(edge.target);
    if (!source || !target || source.virtualSide === target.virtualSide) return pairs;
    const [erpData, ecomData] = source.virtualSide === 'erp' ? [source, target] : [target, source];
    const pairKey = JSON.stringify([erpData.virtualValue, ecomData.virtualValue]);
    if (seen.has(pairKey)) return pairs;
    seen.add(pairKey);
    return [...pairs, { erp: erpData.virtualValue, ecom: ecomData.virtualValue }];
  }, []);
};

// Request bodies keep the default flags a pair already had
const toMethodMappingsBody = (pairs, savedMappings) => pairs.map(({ erp, ecom }) => {
  const saved = (savedMappings || []).find(m => toValue(m.erpMethod) === erp && toValue(m.ecommerceMethod) === ecom);
  return {
    erpMethod: erp,
    ecommerceMethod: ecom,
    isEcommerceDefault: saved?.isEcommerceDefault ?? false,
    isErpDefault: saved?.isErpDefault ?? false,
  };
});

// Keeps the system IDs a pair was saved with, otherwise takes them from the loaded options;
// codes typed in the panel that neither system knows are sent with empty IDs.
const toSalesPersonMappingsBody = (pairs, savedMappings, options) => {
  const optionId = (side, value) => (options[side] || []).find(option => option.value === value)?.id;
  return pairs.map(({ erp, ecom }) => {
    const saved = (savedMappings || []).find(m =>
      toValue(m.erpSalesPersonCode) === erp && toValue(m.ecommerceSalesPersonCode) === ecom);
    return {
      erpSalesPersonId: saved?.erpSalesPersonId || optionId('erp', erp) || '',
      erpSalesPersonCode: erp,
      ecommerceSalesPersonId: saved?.ecommerceSalesPersonId || optionId('ecom', ecom) || '',
      ecommerceSalesPersonCode: ecom,
    };
  });
};

// Create provider component
export const GraphProvider = ({ children, workflow, selectedEntity }) => {
  const workflowData = workflow && workflow.ai_response;
  const [aiResponse, setAiResponse] = useState(null); // State for ai_response
  const [subscriptionid, setSubscriptionId] = useState(null);
  const [connectorId, setConnectorId] = useState(null);
  const [instanceId, setInstanceId] = useState(null);
  const [erpMethods, setErpMethods] = useState([]);
  const [ecommerceMethods, setEcommerceMethods] = useState([]);
  const [savedMappings, setSavedMappings] = useState([]);
  const [isSaving, setIsSaving] = useState(false); // Spinner state
  const [isLoadingSchema, setIsLoadingSchema] = useState(false); // Schema loading state
  const [isSkeletonLoading, setIsSkeletonLoading] = useState(false); // Skeleton loading state
  
  // Toggle state for ERP to eCommerce vs eCommerce to ERP direction
  const [isErpToEcomm, setIsErpToEcomm] = useState(true); // Default: ERP to eCommerce
  
  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // A saved mapping was removed or edited since the last save (drawn edges carry data.unsaved instead)
  const [hasEditedSavedEdges, setHasEditedSavedEdges] = useState(false);
  const { setDirty, registerSave } = useUnsavedChanges();

  // ── Custom attributes: { erpField, ecomField }[] per entity ────────────────
  const [customAttributes, setCustomAttributes] = useState([]);
  const [shippingMappings, setShippingMappings] = useState([]);
  const [paymentMappings, setPaymentMappings] = useState([]);
  const [salesPersonMappings, setSalesPersonMappings] = useState([]);
  // Pairs of the active virtual entity as last loaded from / saved to the backend (null until loaded)
  const [savedVirtualKeys, setSavedVirtualKeys] = useState(null);
  // Values each system offers for the virtual entities, as { erp: [{ value, label }], ecom: [...] }
  const [shippingOptions, setShippingOptions] = useState(EMPTY_VIRTUAL_OPTIONS);
  const [paymentOptions, setPaymentOptions] = useState(EMPTY_VIRTUAL_OPTIONS);
  const [salesPersonOptions, setSalesPersonOptions] = useState(EMPTY_VIRTUAL_OPTIONS);
  // Track which auto-edge indices were manually removed by the user.
  // These pairs are excluded from the API save and auto-edge re-generation.
  const [removedShippingIndices, setRemovedShippingIndices] = useState(new Set());
  const [removedPaymentIndices,  setRemovedPaymentIndices]  = useState(new Set());
  const [removedSalesPersonIndices, setRemovedSalesPersonIndices] = useState(new Set());

  // Load custom attributes scoped to the current entity+subscription
  useEffect(() => {
    const entityKey = selectedEntity?.entityName || '';
    const storageKey = `customAttributes_${subscriptionid ?? 'default'}_${entityKey}`;
    try {
      const saved = localStorage.getItem(storageKey);
      setCustomAttributes(saved ? JSON.parse(saved) : []);
    } catch (_e) {
      setCustomAttributes([]);
    }
  }, [selectedEntity?.entityName, subscriptionid]);

  /** Add a new ERP ↔ eCommerce attribute pair and persist it. */
  const addCustomAttribute = useCallback(
    (erpField, ecomField) => {
      setCustomAttributes((prev) => {
        const updated = [...prev, { erpField, ecomField }];
        const entityKey = selectedEntity?.entityName || '';
        const storageKey = `customAttributes_${subscriptionid ?? 'default'}_${entityKey}`;
        try {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (_e) {}
        return updated;
      });
    },
    [selectedEntity?.entityName, subscriptionid],
  );
  // ────────────────────────────────────────────────────────────────────────────

  const getLabelsFromWorkflow = useCallback((direction = true) => {

    const businessContext = aiResponse?.business_context || workflowData?.business_context || {};
    const erpFromBC = (typeof businessContext?.erp === 'string' && businessContext.erp.trim()) ? businessContext.erp.trim() : null;
    const ecommerceFromBC = (typeof businessContext?.ecommerce === 'string' && businessContext.ecommerce.trim()) ? businessContext.ecommerce.trim() : null;

    let erpLabel = erpFromBC;
    let ecommerceLabel = ecommerceFromBC;

    if ((!erpLabel || !ecommerceLabel) && Array.isArray(workflowData?.recommended_flows) && workflowData.recommended_flows.length > 0) {
      const firstFlow = workflowData.recommended_flows.find(flow => (flow?.name || flow?.erp) && flow?.ecommerce) || workflowData.recommended_flows[0];
      if (!erpLabel) {
        erpLabel = (firstFlow?.name && typeof firstFlow.name === 'string' && firstFlow.name.trim())
          ? firstFlow.name.trim()
          : (firstFlow?.erp && typeof firstFlow.erp === 'string' && firstFlow.erp.trim())
            ? firstFlow.erp.trim()
            : null;
      }
      if (!ecommerceLabel) {
        ecommerceLabel = (firstFlow?.ecommerce && typeof firstFlow.ecommerce === 'string' && firstFlow.ecommerce.trim())
          ? firstFlow.ecommerce.trim()
          : null;
      }
    }

    if (!erpLabel) erpLabel = 'ERP System';
    if (!ecommerceLabel) ecommerceLabel = 'eCommerce System';

    if (direction) {
      return { customerLabel: erpLabel, clientLabel: ecommerceLabel };
    } else {
      return { customerLabel: ecommerceLabel, clientLabel: erpLabel };
    }
  }, [aiResponse, workflowData]);
  
  // Reload schema when isLoadingSchema changes to true
  useEffect(() => {
    setSubscriptionId(workflow?.ai_response?.subscriptionid ?? null);
    setConnectorId(workflow?.ai_response?.connectorId ?? null);
    setInstanceId(workflow?.ai_response?.instanceId ?? null);

    if (isLoadingSchema && subscriptionid && selectedEntity) {
      // Skip API reload for virtual entities
      if (selectedEntity.isVirtual) {
        setIsLoadingSchema(false);
        return;
      }
      const correctLabels = getLabelsFromWorkflow(isErpToEcomm);

      setLabels(prev => {
        if (prev.customerLabel === correctLabels.customerLabel && prev.clientLabel === correctLabels.clientLabel) {
          return prev;
        }
        return correctLabels;
      });

      getSubscription(subscriptionid)
        .then(response => {
          const endpoints = response.data.endpoints;

          if (Array.isArray(endpoints) && endpoints.length > 0) {
            endpoints.forEach(endpoint => {
              const schemaBody = {
                endpointId: endpoint.id,
                subscriptionId: subscriptionid,
                entityName: selectedEntity?.entityName?.toLowerCase().replace(/\s+/g, ''),
                isForceRefresh: false
              };

              getSchema(schemaBody)
                .then(() => {
                  const mapBody = {
                    endpointId: endpoint.id,
                    subscriptionId: subscriptionid,
                    entityName: selectedEntity?.entityName?.toLowerCase().replace(/\s+/g, ''),
                  };

                  getMapping(mapBody)
                    .then(() => {
                      setIsLoadingSchema(false);
                    })
                    .catch(error => {
                      console.error('Error loading mappings:', error);
                      setIsLoadingSchema(false);
                      setSchemaError('Failed to load mappings');
                    });
                })
                .catch(error => {
                  console.error('Error loading schema:', error);
                  setIsLoadingSchema(false);
                  setSchemaError('Failed to load schema');
                });
            });
          } else {
            console.error('No endpoints found in subscription');
            setIsLoadingSchema(false);
            setSchemaError('No endpoints found in subscription');
          }
        })
        .catch(error => {
          console.error('Error loading subscription details:', error);
          setIsLoadingSchema(false);
          setSchemaError('Failed to load subscription details');
        });
    }
  }, [getLabelsFromWorkflow, isErpToEcomm, isLoadingSchema, selectedEntity, subscriptionid, workflow]);
  const [message, setMessage] = useState(null); // Message state
  const [schemaError, setSchemaError] = useState(null); // Schema error state
  
  // Cache the original system names to avoid losing them
  const [originalSystemNames, setOriginalSystemNames] = useState({ erp: null, ecommerce: null });
  
  // Extract and cache system names when workflow data is available
  useEffect(() => {
    if (Array.isArray(workflowData?.recommended_flows) && workflowData.recommended_flows.length > 0) {
      const firstFlow = workflowData.recommended_flows.find(
        flow => (flow.name || flow.erp) && flow.ecommerce
      );

      if (firstFlow) {
        const erpLabel = (firstFlow.name && typeof firstFlow.name === 'string' && firstFlow.name.trim())
          ? firstFlow.name.trim()
          : (firstFlow.erp && typeof firstFlow.erp === 'string' && firstFlow.erp.trim())
            ? firstFlow.erp.trim()
            : null;
        const ecommerceLabel = (firstFlow.ecommerce && typeof firstFlow.ecommerce === 'string' && firstFlow.ecommerce.trim())
          ? firstFlow.ecommerce.trim()
          : null;

        if (erpLabel && ecommerceLabel) {
          setOriginalSystemNames(prev => {
            const newNames = { erp: erpLabel, ecommerce: ecommerceLabel };
            return newNames;
          });
        }
      }
    }
  }, [workflowData]);

  // Initialize labels state dynamically from business_context (Supabase/workflow)
  const [labels, setLabels] = useState(() => {
    return getLabelsFromWorkflow(isErpToEcomm);
  });

  // Update labels when direction changes (recalculate dynamically)
  useEffect(() => {
    const newLabels = getLabelsFromWorkflow(isErpToEcomm);
    setLabels(prev => {
      if (prev.customerLabel === newLabels.customerLabel && prev.clientLabel === newLabels.clientLabel) {
        return prev;
      }
      return newLabels;
    });
  }, [getLabelsFromWorkflow, isErpToEcomm]);

  // Save labels to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('graph_labels', JSON.stringify(labels));
    } catch (e) {
      console.error('Failed to save labels to localStorage:', e);
    }
  }, [labels]);
  
  // Load labels from localStorage on component mount
  useEffect(() => {
    try {
      const savedLabels = localStorage.getItem('graph_labels');
      if (savedLabels) {
        const parsedLabels = JSON.parse(savedLabels);
        // Just apply saved labels; direction will be handled by toggle/effect
        setLabels(parsedLabels);
      }
    } catch (e) {
      console.error('Failed to load labels from localStorage:', e);
    }
  }, []);

  const getInitialNodes = () => {
    const erpSystemName = labels?.customerLabel && isErpToEcomm ? labels.customerLabel : (labels?.clientLabel && !isErpToEcomm ? labels.clientLabel : 'ERP System');
    const ecommerceSystemName = labels?.clientLabel && isErpToEcomm ? labels.clientLabel : (labels?.customerLabel && !isErpToEcomm ? labels.customerLabel : 'eCommerce System');
    
    return [
      {
        id: 'customer',
        type: 'universal',
        data: { 
          label: isErpToEcomm ? erpSystemName : ecommerceSystemName,
          isRootNode: true,
          isParent: false,
          hasChildren: false,
          isLeaf: true
        },
        position: { x: 100, y: 100 }
      },
      {
        id: 'client',
        type: 'universal',
        data: { 
          label: isErpToEcomm ? ecommerceSystemName : erpSystemName,
          isRootNode: true,
          isParent: false,
          hasChildren: false,
          isLeaf: true
        },
        position: { x: 500, y: 100 }
      }
    ];
  };

  const [nodes, setNodes, onNodesChange] = useNodesState(getInitialNodes());
  const [fieldEdges, setFieldEdges, onFieldEdgesChange] = useEdgesState(initialFieldEdges);

  // Toggle function to switch between ERP to eCommerce and eCommerce to ERP
  const toggleDirection = useCallback(() => {
    setNodes([]);
    setFieldEdges([]);
    setInitialLoadComplete(false);
    setHasUnsavedChanges(false);

    setIsErpToEcomm(prev => {
      const newValue = !prev;
      const recalculated = getLabelsFromWorkflow(newValue);

      setLabels(recalculated);

      if (subscriptionid && selectedEntity && !selectedEntity?.isVirtual) {
        try {
          localStorage.setItem('graph_labels', JSON.stringify(recalculated));
        } catch (e) {
          console.error('Failed to save labels to localStorage:', e);
        }

        setNodes([
          {
            id: 'customer',
            data: {
              label: recalculated.customerLabel || 'Customer System',
              isRootNode: true,
              isCollapsed: false
            },
            position: { x: 0, y: 0 }
          },
          {
            id: 'client',
            data: {
              label: recalculated.clientLabel || 'Client System',
              isRootNode: true,
              isCollapsed: false
            },
            position: { x: 0, y: 0 }
          }
        ]);

        setTimeout(() => {
          setIsLoadingSchema(true);
        }, 100);
      }

      return newValue;
    });
  }, [getLabelsFromWorkflow, selectedEntity, setFieldEdges, setNodes, subscriptionid]);

  const showSkeletonTree = useCallback((currentLabels = labels) => {
    setIsSkeletonLoading(true);
    const { nodes: skeletonNodes, edges: skeletonEdges } = generateSkeletonTree(currentLabels);
    setNodes(skeletonNodes);
    setFieldEdges(skeletonEdges);
  }, [labels, setNodes, setFieldEdges]);

  const hideSkeletonTree = useCallback(() => {
    setIsSkeletonLoading(false);
  }, []);

  useEffect(() => {
    setAiResponse(workflow && workflow.ai_response);
    setSubscriptionId(workflow?.ai_response?.subscriptionid ?? null);
  }, [workflow, workflowData]);

  useEffect(() => {
    if (isLoadingSchema) {
      showSkeletonTree(labels);
    } else {
      hideSkeletonTree();
    }
  }, [isLoadingSchema, labels, showSkeletonTree, hideSkeletonTree]);

  useEffect(() => {
    // Case 1: entity has existing saved mappings — mark load done and clear dirty flag
    if (!initialLoadComplete && savedMappings.length > 0) {
      setInitialLoadComplete(true);
      setHasUnsavedChanges(false);
    }
  }, [initialLoadComplete, savedMappings]);

  useEffect(() => {
    // Case 2: schema finished loading for a non-virtual entity with NO existing mappings.
    // Without this, initialLoadComplete would stay false forever and the save
    // button would never become active for brand-new entities.
    if (!isLoadingSchema && !selectedEntity?.isVirtual && !initialLoadComplete) {
      setInitialLoadComplete(true);
      setHasUnsavedChanges(false);
    }
    // Case 3: virtual entities have no loading phase — mark ready immediately
    // so the [nodes, fieldEdges, initialLoadComplete] effect can react to connections.
    // Done in a separate useEffect so React sees the false→true transition properly.
    if (selectedEntity?.isVirtual && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [isLoadingSchema, selectedEntity, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      setHasUnsavedChanges(true);
    }
  }, [nodes, fieldEdges, initialLoadComplete]);

  // Remove dependency on 'nodes' before initialization
  // Use a separate state for rawNodes to avoid referencing 'nodes' before it's defined
  const [rawNodes] = useState([]);
 
  // Recursive helper function to process nested subProperties
  const processNestedSubProperties = useCallback((subProperties, basePath, parentType, methodIndex, nodes, parentNodeId, isErpToEcomm) => {
    if (!Array.isArray(subProperties)) {
      return;
    }

    subProperties.forEach((subProp, subIndex) => {
      if (subProp && subProp.name) {
        const determineFieldType = (subProp) => {
          return subProp.type || 
                 subProp.dataType || 
                 subProp.fieldType || 
                 subProp.data_type ||
                 subProp.field_type ||
                 'string';
        };

        const fieldType = determineFieldType(subProp);

        const currentPath = basePath ? `${basePath}.${subProp.name}` : subProp.name;
        const nodeId = `${parentType}-${currentPath.replace(/[^a-zA-Z0-9]/g, '_')}-${methodIndex}-${subIndex}`;
        const label = subProp.name;
        nodes.push({
          id: nodeId,
          data: { 
            label: label,
            parentId: parentNodeId,
            isMethod: false,
            methodType: parentType,
            fieldType: fieldType,
            isRequired: subProp.isRequired || false,
            description: subProp.description || '', 
            originalSubProperty: subProp,
            fullPath: currentPath
          },
          position: { x: 0, y: 0 }
        });

        if (subProp.subProperties && Array.isArray(subProp.subProperties) && subProp.subProperties.length > 0) {
          processNestedSubProperties(
            subProp.subProperties,
            currentPath,
            parentType,
            methodIndex,
            nodes,
            nodeId,
            isErpToEcomm
          );
        }
      }
    });
  }, []);

  // Enhanced helper function to extract subProperties with full nested dot notation support
  const extractSubProperties = useCallback((methods, parentType, isErpToEcomm) => {
    const nodes = [];
    
    if (!Array.isArray(methods)) {
      return nodes;
    }
    
    methods.forEach((method, methodIndex) => {
      const determineFieldType = (method) => {
        return method.type || 
               method.dataType || 
               method.fieldType || 
               method.data_type ||
               method.field_type ||
               'string';
      };

      const fieldType = determineFieldType(method);

      const nodeId = `${parentType}-${method.name.replace(/[^a-zA-Z0-9]/g, '_')}-${methodIndex}`;
      
      let parentId;
      if (isErpToEcomm) {
        parentId = parentType === 'erp' ? 'customer' : 'client';
      } else {
        parentId = parentType === 'ecommerce' ? 'customer' : 'client';
      }
      
      nodes.push({
        id: nodeId,
        data: { 
          label: method.name,
          parentId: parentId,
          isMethod: true,
          methodType: parentType,
          fieldType: fieldType,
          isRequired: method.isRequired || false,
          description: method.description || '',
          originalMethod: method
        },
        position: { x: 0, y: 0 }
      });

      if (method.subProperties && Array.isArray(method.subProperties) && method.subProperties.length > 0) {
        processNestedSubProperties(
          method.subProperties,
          method.name,
          parentType,
          methodIndex,
          nodes,
          nodeId,
          isErpToEcomm
        );
      }
    });
    
    return nodes;
  }, [processNestedSubProperties]);

  // Create method nodes from erpMethods and ecommerceMethods with subProperties
  const methodNodes = useMemo(() => {
    
    const nodes = [];
    
    const erpSubPropertyNodes = extractSubProperties(erpMethods, 'erp', isErpToEcomm);
    nodes.push(...erpSubPropertyNodes);
    
    const ecomSubPropertyNodes = extractSubProperties(ecommerceMethods, 'ecommerce', isErpToEcomm);
    nodes.push(...ecomSubPropertyNodes);
    
    return nodes;
  }, [erpMethods, ecommerceMethods, extractSubProperties, isErpToEcomm]);

  // ── Helper: strips node-ID cruft from stored method names so only the
  //    human-readable attribute name is shown in the canvas.
  //    e.g. "shipping-erp-shipping_erp_pincode_5-5" → "pincode"
  //         "pincode" → "pincode" (unchanged)
  const cleanNodeLabel = useCallback((value) => {
    if (value === null || value === undefined) return '';
    let result = String(value);
    // 1. Strip outer node-ID wrapper:  "(type)-(dir)-(name)-(index)"
    const outerMatch = /^(?:shipping|payment|salesperson|custom)-(?:erp|ecom(?:merce)?)-(.+)-\d+$/.exec(result);
    if (outerMatch) result = outerMatch[1];
    // 2. Strip sanitized inner prefixes like "shipping_erp_", "payment_ecom_", etc.
    result = result.replace(/^(?:shipping|payment|salesperson|custom)_(?:erp|ecom(?:merce)?)_/i, '');
    // 3. Strip trailing index suffix like "_5"
    result = result.replace(/_\d+$/, '');
    return result || value; // never return empty
  }, []);

  // ── Leaf nodes for each custom attribute pair ───────────────────────────────
  const customAttributeNodes = useMemo(() => {
    const result = [];
    const sanitize = (s) => (s ? String(s).replace(/[^a-zA-Z0-9]/g, '_') : 'unknown');

    (customAttributes || []).forEach(({ erpField, ecomField }, index) => {
      const erpParentId  = isErpToEcomm ? 'customer' : 'client';
      const ecomParentId = isErpToEcomm ? 'client'   : 'customer';

      result.push({
        id: `custom-erp-${sanitize(erpField)}-${index}`,
        data: {
          label: cleanNodeLabel(erpField),
          parentId: erpParentId,
          isMethod: true,
          methodType: isErpToEcomm ? 'erp' : 'ecommerce',
          fieldType: 'string',
          isRequired: false,
          description: 'Custom attribute',
          isCustom: true,
        },
        position: { x: 0, y: 0 },
      });

      result.push({
        id: `custom-ecom-${sanitize(ecomField)}-${index}`,
        data: {
          label: cleanNodeLabel(ecomField),
          parentId: ecomParentId,
          isMethod: true,
          methodType: isErpToEcomm ? 'ecommerce' : 'erp',
          fieldType: 'string',
          isRequired: false,
          description: 'Custom attribute',
          isCustom: true,
        },
        position: { x: 0, y: 0 },
      });
    });

    return result;
  }, [customAttributes, isErpToEcomm, cleanNodeLabel]);

  // ── Nodes for the active virtual entity (Payment / Shipping / Sales Person) ──
  const virtualKey = selectedEntity?.isVirtual ? (selectedEntity.entityName || '').toLowerCase() : '';
  const virtualConfig = VIRTUAL_CONFIG[virtualKey] || null;
  const [virtualOptions, virtualMappings, removedVirtualIndices] =
    virtualKey === 'shipping' ? [shippingOptions, shippingMappings, removedShippingIndices]
      : virtualKey === 'payment' ? [paymentOptions, paymentMappings, removedPaymentIndices]
      : virtualKey === 'sales person' ? [salesPersonOptions, salesPersonMappings, removedSalesPersonIndices]
      : [EMPTY_VIRTUAL_OPTIONS, EMPTY_LIST, EMPTY_SET];
  const virtualGraph = useMemo(
    () => buildVirtualGraph(virtualConfig, virtualOptions, virtualMappings, isErpToEcomm, cleanNodeLabel),
    [virtualConfig, virtualOptions, virtualMappings, isErpToEcomm, cleanNodeLabel],
  );
  // ────────────────────────────────────────────────────────────────────────────

  // Load the active virtual entity's available values (nodes) and saved mappings (edges)
  // whenever it is selected AND IDs are available.
  // This handles the page-refresh race where selectedEntity is set before workflow loads.
  useEffect(() => {
    if (!selectedEntity?.isVirtual || !connectorId) return;
    const entityName = selectedEntity.entityName?.toLowerCase();
    // A failed mappings request keeps the current mappings; the options helpers never reject.
    const loadMappings = (request) => request
      .then(res => (Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error(`Failed to load ${entityName} mappings:`, err);
        return null;
      });

    if (entityName === 'shipping' && instanceId) {
      Promise.all([loadMappings(getShippingMappings(connectorId, instanceId)), getShippingOptions(connectorId, instanceId)])
        .then(([mappings, options]) => {
          setShippingOptions(options.data);
          setSavedVirtualKeys(toVirtualPairKeys(VIRTUAL_CONFIG.shipping, mappings));
          if (mappings) setShippingMappings(mappings);
        });
    } else if (entityName === 'payment' && instanceId) {
      Promise.all([loadMappings(getPaymentMappings(connectorId, instanceId)), getPaymentOptions(connectorId, instanceId)])
        .then(([mappings, options]) => {
          setPaymentOptions(options.data);
          setSavedVirtualKeys(toVirtualPairKeys(VIRTUAL_CONFIG.payment, mappings));
          if (mappings) setPaymentMappings(mappings);
        });
    } else if (entityName === 'sales person') {
      // Sales person data is scoped to the connection only, so no instanceId is needed.
      Promise.all([loadMappings(getSalesPersonMappings(connectorId)), getSalesPersonOptions(connectorId)])
        .then(([mappings, options]) => {
          setSalesPersonOptions(options.data);
          setSavedVirtualKeys(toVirtualPairKeys(VIRTUAL_CONFIG['sales person'], mappings));
          if (mappings) setSalesPersonMappings(mappings);
        });
    }
  }, [selectedEntity, connectorId, instanceId]);

  // Auto-generate an edge for each saved/added pair of the active virtual entity whenever
  // the pairs, the value nodes or the direction change.
  useEffect(() => {
    if (!virtualConfig) return;
    const autoEdgePrefix = `${virtualConfig.prefix}-auto-edge-`;
    const autoEdges = buildVirtualAutoEdges(virtualConfig, virtualMappings, removedVirtualIndices, virtualGraph.idByValue, isErpToEcomm, savedVirtualKeys);
    setFieldEdges(prev => {
      const nonAutoEdges = prev.filter(e => !e.id.startsWith(autoEdgePrefix));
      if (autoEdges.length === 0 && nonAutoEdges.length === prev.length) return prev;
      return [...nonAutoEdges, ...autoEdges];
    });
  }, [virtualConfig, virtualMappings, removedVirtualIndices, virtualGraph, isErpToEcomm, savedVirtualKeys, setFieldEdges]);

  // Always use the latest labels for root nodes, even after updates
  const processedNodes = useMemo(() => {
    // Ensure root nodes are always present with complete data structure
    // Set fallback labels based on direction toggle
    const fallbackCustomerLabel = isErpToEcomm ? 'ERP System' : 'eCommerce System';
    const fallbackClientLabel = isErpToEcomm ? 'eCommerce System' : 'ERP System';
    
    const customerLabel = labels.customerLabel || fallbackCustomerLabel;
    const clientLabel = labels.clientLabel || fallbackClientLabel;

    // Only include mapping nodes relevant to the currently active entity
    // to prevent cross-entity nodes from appearing on the canvas.
    // virtualGraph only ever holds the active Payment / Shipping / Sales Person value nodes.
    const activeVirtualNodes   = virtualGraph.nodes;
    const activeCustomNodes    = (selectedEntity?.isVirtual && !virtualConfig) ? customAttributeNodes : [];
    
    const rootNodes = [
      {
        id: 'customer',
        type: 'universal',
        data: { 
          label: customerLabel,
          isRootNode: true,
          isParent: true,
          hasChildren: methodNodes.filter(n => n.data.parentId === 'customer').length > 0
            || activeCustomNodes.filter(n => n.data.parentId === 'customer').length > 0
            || activeVirtualNodes.filter(n => n.data.parentId === 'customer').length > 0,
          isLeaf: false
        },
        position: { x: 100, y: 50 }
      },
      {
        id: 'client',
        type: 'universal', 
        data: { 
          label: clientLabel,
          isRootNode: true,
          isParent: true,
          hasChildren: methodNodes.filter(n => n.data.parentId === 'client').length > 0
            || activeCustomNodes.filter(n => n.data.parentId === 'client').length > 0
            || activeVirtualNodes.filter(n => n.data.parentId === 'client').length > 0,
          isLeaf: false
        },
        position: { x: 700, y: 50 }
      }
    ];

    // Merge root nodes with rawNodes, methodNodes and active mapping nodes only
    const mergedNodes = rootNodes.map(rootNode => {
      const existingNode = rawNodes.find(node => node.id === rootNode.id);
      return existingNode ? { ...existingNode, data: { ...existingNode.data, label: rootNode.data.label } } : rootNode;
    })
    .concat(rawNodes.filter(node => !['customer', 'client'].includes(node.id)))
    .concat(methodNodes)           // Add method nodes
    .concat(activeCustomNodes)     // Add custom attribute leaf nodes (active entity only)
    .concat(activeVirtualNodes);   // Add Payment / Shipping / Sales Person value nodes (active entity only)

    // Apply type inference and auto positioning
    const finalNodes = autoPositionNodes(inferNodeTypesMultilevel(mergedNodes));
    
    // Debug: If no nodes, return at least the root nodes
    if (finalNodes.length === 0) {
      return [
        {
          id: 'customer',
          type: 'universal',
          data: { 
            label: customerLabel,
            isRootNode: true,
            isParent: false,
            hasChildren: false,
            isLeaf: true
          },
          position: { x: 100, y: 100 }
        },
        {
          id: 'client',
          type: 'universal',
          data: { 
            label: clientLabel,
            isRootNode: true,
            isParent: false,
            hasChildren: false,
            isLeaf: true
          },
          position: { x: 500, y: 100 }
        }
      ];
    }
    
    return finalNodes;
  }, [labels, rawNodes, methodNodes, customAttributeNodes, virtualGraph, virtualConfig, isErpToEcomm, selectedEntity]);

  useEffect(() => {
    if (processedNodes.length > 0) {
      setNodes(processedNodes);
    }
  }, [processedNodes, setNodes]);
  
  // Force update nodes when toggle changes to ensure labels are refreshed
  useEffect(() => {
    if (processedNodes.length > 0) {
      const updatedNodes = processedNodes.map(node => ({
        ...node,
        data: { ...node.data },
        __timestamp: Date.now()
      }));
      setNodes(updatedNodes);
    }
  }, [isErpToEcomm, processedNodes, setNodes]);

  // State to store all connected mappings
  const [connectedMappings, setConnectedMappings] = useState([]);
  
  // Generate tree edges automatically from parent-child relationships
  const treeEdges = useMemo(() => generateTreeEdges(nodes), [nodes]);
  
  // Combine both types of edges for rendering
  const allEdges = useMemo(() => [...treeEdges, ...fieldEdges], [treeEdges, fieldEdges]);
  
  // Handle edge changes only for field edges (not tree edges)
  const onEdgesChange = useCallback((changes) => {
    // Filter out changes that would affect tree edges
    const fieldEdgeChanges = changes.filter(change => 
      !change.id || !change.id.startsWith('tree-edge-')
    );
    
    onFieldEdgesChange(fieldEdgeChanges);
  }, [onFieldEdgesChange]);
  
  // Check if a connection is valid: allow connections between leaf nodes and directly to root nodes
  const isValidConnection = useCallback((connection) => {
    // Get source and target nodes
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    
    if (!sourceNode || !targetNode) return false;
    
    // Check if source is a leaf node (no children)
    const isSourceLeaf = !sourceNode.data.hasChildren;
    
    const isTargetLeaf = !targetNode.data.hasChildren;
    
    // Check if source is a root node (customer or client)
    const isSourceRoot = sourceNode.id === 'customer' || sourceNode.id === 'client';
    
    // Check if target is a root node (customer or client)
    const isTargetRoot = targetNode.id === 'customer' || targetNode.id === 'client';
    
    // Find root system for each node
    const findRootId = (node) => {
      if (!node) return null;
      if (node.id === 'customer' || node.id === 'client') return node.id;
      let currentNode = node;
      while (currentNode && currentNode.data.parentId) {
        const nextParentId = currentNode.data.parentId;
        const parentNode = nodes.find(n => n.id === nextParentId);
        if (!parentNode) break;
        if (parentNode.id === 'customer' || parentNode.id === 'client') return parentNode.id;
        currentNode = parentNode;
      }
      return null;
    };
    
    const sourceRootId = findRootId(sourceNode);
    const targetRootId = findRootId(targetNode);
    
    // Prevent mapping nodes from the same root system
    if (sourceRootId && targetRootId && sourceRootId === targetRootId) {
      return false;
    }

    // Check basic connection rules first
    const basicConnectionValid = (isSourceLeaf && isTargetLeaf) || 
                                 (isSourceLeaf && isTargetRoot) || 
                                 (isSourceRoot && isTargetLeaf);
    
    if (!basicConnectionValid) return false;
    
    // Check for many-to-one mapping (prevent multiple sources mapping to same target)
    const existingTargetEdges = fieldEdges.filter(edge => edge.target === connection.target);
    if (existingTargetEdges.length > 0) {
      // If target already has an incoming edge from a different source, reject
      const differentSourceExists = existingTargetEdges.some(edge => edge.source !== connection.source);
      if (differentSourceExists) {
        const errorMessage = `Cannot map to "${targetNode.data.label}"!\n\nThis field is already mapped from another field. One field can only be mapped from one source.`;
        setMessage({
          type: 'error',
          title: 'Multiple Mappings Not Allowed',
          content: errorMessage
        });
        return false;
      }
    }
    
    // For root connections, skip data type validation
    if (isSourceRoot || isTargetRoot) return true;
    
    // Data type validation for leaf-to-leaf connections
    const sourceFieldType = sourceNode.data.fieldType;
    const targetFieldType = targetNode.data.fieldType;

    // If either node doesn't have a fieldType, allow connection (backward compatibility)
    if (!sourceFieldType || !targetFieldType) {
      return true;
    }
    
    // Normalize data types for comparison (case-insensitive) with common synonyms
    const normalizeType = (type) => {
      if (!type) return 'string';
      const t = type.toLowerCase().trim();
      const map = {
        // String-like
        'varchar': 'string',
        'text': 'string',
        'char': 'string',
        'character varying': 'string',
        // Integer-like
        'int': 'integer',
        'smallint': 'integer',
        'bigint': 'integer'
      };
      return map[t] || t;
    };
    
    const normalizedSourceType = normalizeType(sourceFieldType);
    const normalizedTargetType = normalizeType(targetFieldType);

    // Check for specific incompatible data type combinations
    const isIncompatibleConnection = (sourceType, targetType) => {
      // Boolean source restrictions
      if (sourceType === 'boolean' && ['decimal', 'integer', 'int', 'static'].includes(targetType)) {
        return true;
      }
      
      // Integer source restrictions
      if (sourceType === 'integer' && targetType === 'static') {
        return true;
      }
      
      // Decimal source restrictions
      if (sourceType === 'decimal' && ['static', 'integer', 'int'].includes(targetType)) {
        return true;
      }
      
      // Date source restrictions
      if (sourceType === 'date' && ['integer', 'int', 'decimal', 'static'].includes(targetType)) {
        return true;
      }
      
      // DateTime source restrictions
      if (sourceType === 'datetime' && ['integer', 'int', 'decimal', 'static'].includes(targetType)) {
        return true;
      }
      
      return false;
    };
    
    // Check for incompatible combinations first
    if (isIncompatibleConnection(normalizedSourceType, normalizedTargetType)) {
      // Helper function to find the root system for a node
      const findRootSystem = (node) => {
        if (!node) return 'Unknown System';
        
        // If it's already a root node
        if (node.id === 'customer') return labels.customerLabel || 'ERP System';
        if (node.id === 'client') return labels.clientLabel || 'eCommerce System';
        
        // Traverse up the hierarchy to find the root
        let currentNode = node;
        while (currentNode && currentNode.data.parentId) {
          const nextParentId = currentNode.data.parentId;
          const parentNode = nodes.find(n => n.id === nextParentId);
          if (!parentNode) break;
          
          if (parentNode.id === 'customer') return labels.customerLabel || 'ERP System';
          if (parentNode.id === 'client') return labels.clientLabel || 'eCommerce System';
          
          currentNode = parentNode;
        }
        
        return 'Unknown System';
      };
      
      const sourceSystemLabel = findRootSystem(sourceNode);
      const targetSystemLabel = findRootSystem(targetNode);
      
      // Show popup message with detailed information
      const errorMessage = `Connection not allowed!\n\nSource: "${sourceNode.data.label}" (${sourceFieldType}) from ${sourceSystemLabel}\nTarget: "${targetNode.data.label}" (${targetFieldType}) from ${targetSystemLabel}\n\nThis data type combination is not compatible.`;
      
      // Set the message state to show popup
      setMessage({
        type: 'error',
        title: 'Incompatible Data Types',
        content: errorMessage
      });
      
      return false;
    }
    
    // Check if types are exactly the same (strict matching)
    const isExactMatch = normalizedSourceType === normalizedTargetType;
    
    if (!isExactMatch) {
      // Helper function to find the root system for a node
      const findRootSystem = (node) => {
        if (!node) return 'Unknown System';
        
        // If it's already a root node
        if (node.id === 'customer') return labels.customerLabel || 'ERP System';
        if (node.id === 'client') return labels.clientLabel || 'eCommerce System';
        
        // Traverse up the hierarchy to find the root
        let currentNode = node;
        while (currentNode && currentNode.data.parentId) {
          const nextParentId = currentNode.data.parentId;
          const parentNode = nodes.find(n => n.id === nextParentId);
          if (!parentNode) break;
          
          if (parentNode.id === 'customer') return labels.customerLabel || 'ERP System';
          if (parentNode.id === 'client') return labels.clientLabel || 'eCommerce System';
          
          currentNode = parentNode;
        }
        
        return 'Unknown System';
      };
      
      const sourceSystemLabel = findRootSystem(sourceNode);
      const targetSystemLabel = findRootSystem(targetNode);
      
      // Show popup message with detailed information
      const errorMessage = `Connection not allowed!\n\nSource: "${sourceNode.data.label}" (${sourceFieldType}) from ${sourceSystemLabel}\nTarget: "${targetNode.data.label}" (${targetFieldType}) from ${targetSystemLabel}\n\nOnly fields with matching data types can be connected.`;
      
      // Set the message state to show popup
      setMessage({
        type: 'error',
        title: 'Data Type Mismatch',
        content: errorMessage
      });
      
      return false;
    }
    
    return true;
  }, [fieldEdges, labels, nodes, setMessage]);
  
  // Find all descendant nodes of a given node (recursively)
  const findAllDescendants = useCallback((nodeId) => {
    const result = [];
    
    const processNode = (id) => {
      const children = nodes
        .filter(n => n.data.parentId === id)
        .map(n => n.id);
      
      result.push(...children);
      
      // Recursively process children
      children.forEach(childId => processNode(childId));
    };
    
    processNode(nodeId);
    return result;
  }, [nodes]);
  
  // Handle node collapse
  const handleNodeCollapse = useCallback((nodeId) => {
    setNodes(prevNodes => {
      // First find the node to collapse and get its current state
      const nodeToCollapse = prevNodes.find(n => n.id === nodeId);
      if (!nodeToCollapse) return prevNodes;
      
      const newIsCollapsed = !nodeToCollapse.data.isCollapsed;
      
      // Find all descendant nodes
      const allDescendantIds = findAllDescendants(nodeId);
      
      // Check for mapping edges connected to any descendant node
      const hasConnectedEdges = (nodeId) => {
        return fieldEdges.some(edge => 
          edge.source === nodeId || edge.target === nodeId
        );
      };
      
      // Update all nodes
      return prevNodes.map(node => {
        // Update the collapsed node
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              isCollapsed: newIsCollapsed
            }
          };
        } 
        // Process descendant nodes
        else if (allDescendantIds.includes(node.id)) {
          const hasEdgeConnection = hasConnectedEdges(node.id);
          
          // If parent is collapsed, hide this node unless it has connections
          // Also, if this is a parent node, we need to ensure it's also marked as collapsed
          if (node.data.hasChildren) {
            return {
              ...node,
              data: {
                ...node.data,
                isCollapsed: newIsCollapsed // Inherit parent's collapsed state
              },
              hidden: newIsCollapsed && !hasEdgeConnection
            };
          } else {
            // Leaf nodes just get hidden
            return {
              ...node,
              hidden: newIsCollapsed && !hasEdgeConnection
            };
          }
        }
        return node;
      });
    });
  }, [setNodes, fieldEdges, findAllDescendants]);

  // Simplified function for group mappings (no child suggestions)
  const suggestChildMappings = useCallback((sourceGroupId, targetGroupId) => {
    // This function is kept as a placeholder but doesn't create child mappings
    // No child mappings are created as per requirements
  }, []);

  // Connect nodes
  const handleConnect = useCallback((params) => {
    // Check if this connection already exists
    const existingConnection = fieldEdges.find(edge => 
      edge.source === params.source && edge.target === params.target
    );
    
    if (existingConnection) {
      return;
    }
    
    // First check if this is a valid connection
    if (!isValidConnection({
      source: params.source,
      target: params.target
    })) {
      return;
    }
    
    // Get source and target nodes
    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);

    const isSourceRoot = sourceNode.id === 'customer' || sourceNode.id === 'client';
    const isTargetRoot = targetNode.id === 'customer' || targetNode.id === 'client';
    const isRootConnection = isSourceRoot || isTargetRoot;

    const edgeLabel = (() => {
      // Show full path if available for both source and target
      const sourceLabel = sourceNode.data.fullPath || sourceNode.data.label;
      const targetLabel = targetNode.data.fullPath || targetNode.data.label;
      return `${sourceLabel} -> ${targetLabel}`;
    })();

    const newEdge = {
      ...params,
      type: isRootConnection ? 'root-connection' : 'custom',
      animated: true,
      style: isRootConnection
        ? {
            stroke: EDGE_COLOR,
            strokeWidth: 2,
            strokeDasharray: '5 5'
          }
        : { ...DEFAULT_EDGE_STYLE },
      data: {
        label: params.data?.label || edgeLabel,
        transformationType: 'none',
        hasTransformation: false,
        isRootConnection: isRootConnection,
        // Drawn but not saved yet: shown in red until the mappings are saved
        unsaved: !isRootConnection
      }
    };

    setFieldEdges(prevEdges => {
      const updatedEdges = addEdge(newEdge, prevEdges);
      return updatedEdges;
    });
    
    // Add the connection to connectedMappings with detailed data
    setConnectedMappings(prevMappings => [
      ...prevMappings,
      {
        ecommerceAttributeName: targetNode.data.label,
        ecommerceAttributeDataType: targetNode.data.dataType || "text", // Replace with actual data if available
        ecommerceAttributeSize: targetNode.data.size || "0", // Replace with actual data if available
        ecommerceAttributeType: targetNode.data.type || "default", // Replace with actual data if available
        erpAttributeName: sourceNode.data.label,
        erpAttributeDataType: sourceNode.data.dataType || "Text", // Replace with actual data if available
        erpAttributeSize: sourceNode.data.size || "0", // Replace with actual data if available
        canUpdate: true, // Replace with actual data if available
        canDelete: true, // Replace with actual data if available
        erpIsRequired: true, // Replace with actual data if available
        ecommerceIsRequired: true, // Replace with actual data if available
        default: false // Replace with actual data if available
      }
    ]);
  }, [fieldEdges, isValidConnection, nodes, setConnectedMappings, setFieldEdges]);
  
  // Function to select a node (highlight it)
  const selectNode = useCallback((nodeId) => {
    setNodes(prevNodes => {
      return prevNodes.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              selected: true
            }
          };
        } else if (node.data.selected) {
          return {
            ...node,
            data: {
              ...node.data,
              selected: false
            }
          };
        }
        return node;
      });
    });
  }, [setNodes]);
  
  // Function to update edge label
  const updateEdgeLabel = useCallback((edgeId, label) => {
    setHasEditedSavedEdges(true);
    setFieldEdges(prevEdges => {
      return prevEdges.map(edge => {
        if (edge.id === edgeId) {
          return {
            ...edge,
            data: {
              ...edge.data,
              label
            }
          };
        }
        return edge;
      });
    });
  }, [setFieldEdges]);
  
  // Function to set edge transformation
  const setEdgeTransformation = useCallback((edgeId, transformationType, options = {}) => {
    setHasEditedSavedEdges(true);
    setFieldEdges(prevEdges => {
      return prevEdges.map(edge => {
        if (edge.id === edgeId) {
          return {
            ...edge,
            data: {
              ...edge.data,
              transformationType,
              hasTransformation: transformationType !== 'none',
              options: options
            }
          };
        }
        return edge;
      });
    });
  }, [setFieldEdges]);
  
  // Function to remove an edge
  const removeEdge = useCallback((edgeId) => {
    // Deleting an edge that was never saved changes nothing on the server
    if (!fieldEdges.find(edge => edge.id === edgeId)?.data?.unsaved) {
      setHasEditedSavedEdges(true);
    }
    setFieldEdges(prevEdges => prevEdges.filter(edge => edge.id !== edgeId));

    // If this is an auto-generated shipping/payment/sales person edge, track its index
    // so the auto-edge effect does not re-add it and saveState excludes it.
    const shippingMatch = /^shipping-auto-edge-(\d+)$/.exec(edgeId);
    if (shippingMatch) {
      const idx = Number(shippingMatch[1]);
      setRemovedShippingIndices(prev => new Set([...prev, idx]));
    }
    const paymentMatch = /^payment-auto-edge-(\d+)$/.exec(edgeId);
    if (paymentMatch) {
      const idx = Number(paymentMatch[1]);
      setRemovedPaymentIndices(prev => new Set([...prev, idx]));
    }
    const salesPersonMatch = /^salesperson-auto-edge-(\d+)$/.exec(edgeId);
    if (salesPersonMatch) {
      const idx = Number(salesPersonMatch[1]);
      setRemovedSalesPersonIndices(prev => new Set([...prev, idx]));
    }

    // Remove the connection from connectedMappings
    setConnectedMappings(prevMappings => prevMappings.filter(mapping => {
      const edge = fieldEdges.find(e => e.id === edgeId);
      return !(mapping.source === edge?.source && mapping.target === edge?.target);
    }));
  }, [setFieldEdges, setConnectedMappings, fieldEdges]);
  
  // Function to add a new node
  const addNode = useCallback((side, nodeType, label) => {
    // Prevent automatic creation of client nodes - they should only come from ecommerceMethods
    if (side === 'right' || nodeType === 'client') {
      console.error('❌ Prevented automatic client node creation! Client nodes should only come from ecommerceMethods.');
      return null;
    }
    
    // Generate a unique ID for the new node
    const timestamp = new Date().getTime();
    const randomSuffix = Math.floor(Math.random() * 1000);
    const newNodeId = `${side}-${nodeType}-${timestamp}-${randomSuffix}`;
    
    // Determine the parent ID based on the side
    const parentId = side === 'left' ? 'customer' : 'client';
    
    // Find existing nodes on the same side to determine a good position
    const existingNodes = nodes.filter(node => {
      // Check if this node is on the same side
      if (side === 'left') {
        return node.data.parentId === 'customer' && !node.data.hasChildren;
      } else {
        return node.data.parentId === 'client' && !node.data.hasChildren;
      }
    });
    
    // Calculate a position that's near other nodes on the same side
    let initialPosition;
    
    if (existingNodes.length > 0) {
      const maxY = Math.max(...existingNodes.map(node => node.position.y)) + 70;
      
      initialPosition = { 
        x: side === 'left' ? 100 : 700, 
        y: maxY 
      };
    } else {
      // If no existing nodes, use default position
      initialPosition = { 
        x: side === 'left' ? 100 : 700, 
        y: 200 
      };
    }
    
    // Create the new node
    const newNode = {
      id: newNodeId,
      data: { 
        label: label || `New ${nodeType}`,
        parentId: parentId,
        isCollapsed: false
      },
      position: initialPosition
    };
    
    // Add the new node to the nodes array
    setNodes(prevNodes => {
      // Add the new node to the array
      const updatedNodes = [...prevNodes, newNode];
      
      // Apply node type inference and auto positioning
      return autoPositionNodes(inferNodeTypesMultilevel(updatedNodes));
    });
    
    return newNodeId;
  }, [setNodes, nodes]);

  // Function to apply multiple suggestions at once
  // Fixed to use existing nodes instead of creating duplicates
  const applyMultipleSuggestions = useCallback((suggestions) => {
    if (!suggestions || suggestions.length === 0) {
      console.warn('No suggestions to apply');
      return;
    }

    // Helper function to find a node by matching it with erpMethods/ecommerceMethods
    const findNodeByMethodReference = (nodeArray, fieldName, isSource = true) => {
      let targetMethodInfo = null;

      if (isSource) {
        // For source nodes, find in erpMethods
        targetMethodInfo = erpMethods.find(method => method.name === fieldName);
      } else {
        // For target nodes, handle nested paths like "product.price"
        if (fieldName.includes('.')) {
          const [parentName, childName] = fieldName.split('.');
          const parentMethod = ecommerceMethods.find(method => method.name === parentName);
          if (parentMethod && parentMethod.subProperties) {
            targetMethodInfo = parentMethod.subProperties.find(sub => sub.name === childName);
            if (targetMethodInfo) {
              // For nested properties, we want to find the child node, not the parent
              fieldName = childName;
            }
          }
        } else {
          // If we only have the leaf node name, search in all subProperties
          for (const method of ecommerceMethods) {
            if (method.subProperties) {
              const subProp = method.subProperties.find(sub => sub.name === fieldName);
              if (subProp) {
                targetMethodInfo = subProp;
                break;
              }
            }
          }
          
          // If not found in subProperties, try direct match
          if (!targetMethodInfo) {
            targetMethodInfo = ecommerceMethods.find(method => method.name === fieldName);
          }
        }
      }

      if (!targetMethodInfo) {
        return null;
      }

      // Now find the corresponding node in the nodeArray
      const matchingNode = nodeArray.find(node => {
        const labelMatches = node.data.label === fieldName;
        return labelMatches;
      });

      return matchingNode;
    };

    // Collect all nodes and edges to be added
    let nodesToAdd = [];
    let edgesToAdd = [];
    let workingNodes = [...nodes]; // Working copy of nodes to track what we're adding

    suggestions.forEach((suggestion, index) => {
      const { sourceNode, targetNode, confidence, status } = suggestion;

      if (!sourceNode || !targetNode) {
        console.error('Invalid suggestion: missing sourceNode or targetNode', suggestion);
        return;
      }

      let sourceNodeId, targetNodeId;

      // Check if sourceNode exists in current nodes or nodes we're about to add
      let sourceNodeObj = findNodeByMethodReference(workingNodes, sourceNode, true);
      if (sourceNodeObj) {
        sourceNodeId = sourceNodeObj.id;
      } else {
        sourceNodeId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_src_${index}`;
        
        const sourceNodeData = {
          id: sourceNodeId,
          data: { 
            label: sourceNode,
            parentId: 'customer',
            isCollapsed: false
          },
          position: { x: 100, y: 200 } // Will be repositioned by autoPositionNodes
        };
        nodesToAdd.push(sourceNodeData);
        workingNodes.push(sourceNodeData); // Add to working copy so next suggestions can see it
      }

      // Check if targetNode exists in current nodes or nodes we're about to add
      let targetNodeObj = findNodeByMethodReference(workingNodes, targetNode, false);
      if (targetNodeObj) {
        targetNodeId = targetNodeObj.id;
      } else {
        targetNodeId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_tgt_${index}`;
        
        const targetNodeData = {
          id: targetNodeId,
          data: { 
            label: targetNode,
            parentId: 'client',
            isCollapsed: false
          },
          position: { x: 700, y: 200 } // Will be repositioned by autoPositionNodes
        };
        nodesToAdd.push(targetNodeData);
        workingNodes.push(targetNodeData); // Add to working copy so next suggestions can see it
      }

      // Prepare the connection
      const edgeParams = {
        source: sourceNodeId,
        target: targetNodeId,
        sourceHandle: 'r',
        targetHandle: 'l',
        type: 'custom',
        style: { ...DEFAULT_EDGE_STYLE },
        data: {
          label: `${sourceNode} → ${targetNode}`,
          transformationType: 'none',
          hasTransformation: false,
          confidence,
          status
        }
      };
      edgesToAdd.push(edgeParams);
    });

    // Add all new nodes in a single state update
    if (nodesToAdd.length > 0) {
      setNodes(prevNodes => {
        const updatedNodes = [...prevNodes, ...nodesToAdd];
        return autoPositionNodes(inferNodeTypesMultilevel(updatedNodes));
      });
    }

    // Add all new edges in a single state update
    if (edgesToAdd.length > 0) {
      setFieldEdges(prevEdges => {
        let newEdges = [...prevEdges];
        edgesToAdd.forEach(edgeParams => {
          newEdges = addEdge(edgeParams, newEdges);
        });
        return newEdges;
      });
    }
  }, [nodes, setFieldEdges, erpMethods, ecommerceMethods, setNodes]);

  // Function to apply a single suggestion
  const applySuggestion = useCallback((suggestion) => {
    applyMultipleSuggestions([suggestion]);
  }, [applyMultipleSuggestions]);
  
  // Everything on the canvas is saved: drawn edges go back to the normal colour
  const markEdgesSaved = useCallback(() => {
    setHasEditedSavedEdges(false);
    setFieldEdges(prevEdges => prevEdges.map(edge => (
      edge.data?.unsaved ? { ...edge, data: { ...edge.data, unsaved: false } } : edge
    )));
  }, [setFieldEdges]);

  // Function to save the graph state. Resolves true once saved, false when the save failed.
  const saveState = useCallback(() => {
    setIsSaving(true); // Start spinner

    const state = {
      nodes,
      fieldEdges,
      connectedMappings
    };

    // Filter to only include actual field mapping edges (custom type with data labels)
    const mappingEdges = fieldEdges.filter(edge => {
      const isCustomType = edge.type === 'custom';
      const hasData = !!edge.data;
      const hasLabel = !!(edge.data && edge.data.label);
      const isNotTreeEdge = !edge.id.includes('tree-edge');

      return isCustomType && hasData && hasLabel && isNotTreeEdge;
    });

    // Remove duplicates based on source-target combination
    const uniqueMappings = new Map();
    mappingEdges.forEach(edge => {
      const sourceNode = nodes.find(node => node.id === edge.source);
      const targetNode = nodes.find(node => node.id === edge.target);
      const key = `${sourceNode?.data.label || edge.source}->${targetNode?.data.label || edge.target}`;
      
      // Only keep the first occurrence of each unique mapping
      if (!uniqueMappings.has(key)) {
        uniqueMappings.set(key, edge);
      }
    });
    
    const deduplicatedEdges = Array.from(uniqueMappings.values());
    
    // Helper function to build source path with array type checking (left tree under 'customer')
    function buildSourcePath(node, sourceRootId = 'customer') {
      if (!node) return '';
      if (node.data.parentId && node.data.parentId !== sourceRootId) {
        const parentNode = nodes.find(n => n.id === node.data.parentId);
        if (parentNode) {
          const parentPath = buildSourcePath(parentNode, sourceRootId);
          const isParentArray = parentNode.data.fieldType === 'array' ||
                                parentNode.data.fieldType === 'object[]' ||
                                (parentNode.data.fieldType && parentNode.data.fieldType.toLowerCase().includes('array'));
          if (isParentArray) {
            return parentPath ? `${parentPath}[].${node.data.label}` : `${parentNode.data.label}[].${node.data.label}`;
          } else {
            return parentPath ? `${parentPath}.${node.data.label}` : `${parentNode.data.label}.${node.data.label}`;
          }
        }
        return node.data.label;
      }
      return node.data.label;
    }

    // Group edges by source to detect split mappings (one source to multiple targets)
    const edgesBySource = new Map();
    deduplicatedEdges.forEach(edge => {
      const sourceNode = nodes.find(node => node.id === edge.source);
      const sourcePath = sourceNode ? buildSourcePath(sourceNode, 'customer') : (edge.source || '');
      
      if (!edgesBySource.has(sourcePath)) {
        edgesBySource.set(sourcePath, []);
      }
      edgesBySource.get(sourcePath).push(edge);
    });

    // Helper function to build target path with array type checking
    // Accepts targetRootId to support both ERP→eCommerce ('client') and eCommerce→ERP ('customer')
    function buildTargetPath(node, targetRootId = (isErpToEcomm ? 'client' : 'customer')) {
      if (!node) return '';
      
      // For eCommerce→ERP direction, we need to exclude the system name prefix
      if (!isErpToEcomm) {
        // Build the complete path hierarchy first
        const pathParts = [];
        let currentNode = node;
        
        // Traverse up the hierarchy to build the path
        while (currentNode && currentNode.data.parentId && currentNode.data.parentId !== targetRootId) {
          pathParts.unshift(currentNode.data.label);
          const nextParentId = currentNode.data.parentId;
          currentNode = nodes.find(n => n.id === nextParentId);
        }
        
        // For eCommerce→ERP, we need to identify and remove only the system name (like "NetSuite")
        // but keep the actual field hierarchy (like "matrixoptioncustitem25.refName")

        // Check if the first part is a system name that should be removed
        // Common system names to remove: NetSuite, Magento, etc.
        const systemNames = ['NetSuite', 'Magento', 'netsuite', 'magento'];
        
        if (pathParts.length === 0) {
          // Direct child of target root
          return node.data.label;
        } else if (pathParts.length === 1) {
          // Only one level - check if it's a system name
          if (systemNames.includes(pathParts[0])) {
            return node.data.label;
          } else {
            return pathParts[0];
          }
        } else {
          // Multiple levels - remove system name if it's the first part
          if (systemNames.includes(pathParts[0])) {
            pathParts.shift(); // Remove the system name prefix
            const result = pathParts.join('.');
            return result;
          } else {
            // Keep the full path if it doesn't start with a system name
            const result = pathParts.join('.');
            return result;
          }
        }
      }
      
      // Original logic for ERP→eCommerce direction
      if (node.data.parentId && node.data.parentId !== targetRootId) {
        const parentNode = nodes.find(n => n.id === node.data.parentId);
        if (parentNode) {
          const parentPath = buildTargetPath(parentNode, targetRootId);
          
          // Check if the parent node is an array type
          const isParentArray = parentNode.data.fieldType === 'array' || 
                               parentNode.data.fieldType === 'object[]' ||
                               (parentNode.data.fieldType && parentNode.data.fieldType.toLowerCase().includes('array'));
          
          // Format the path based on parent type
          if (isParentArray) {
            // Use empty array notation instead of [0] index
            return parentPath ? `${parentPath}[].${node.data.label}` : `${parentNode.data.label}[].${node.data.label}`;
          } else {
            return parentPath ? `${parentPath}.${node.data.label}` : `${parentNode.data.label}.${node.data.label}`;
          }
        }
        return node.data.label;
      }
      return node.data.label;
    }

    // Format fieldEdges to the required API format, handling split mappings
    const formattedEdges = [];
    let mappingIndex = 1;

    edgesBySource.forEach((edges, sourcePath) => {
      if (edges.length === 1) {
        // Single target - direct mapping
        const edge = edges[0];
        const sourceNode = nodes.find(node => node.id === edge.source);
        const targetNode = nodes.find(node => node.id === edge.target);
        const targetPath = targetNode ? buildTargetPath(targetNode, isErpToEcomm ? 'client' : 'customer') : edge.target;

        // If direction is eCommerce → ERP, ensure sourcePath has [] for array parents
        const finalSourcePath = isErpToEcomm ? sourcePath : (sourceNode ? buildSourcePath(sourceNode, 'customer') : sourcePath);

        formattedEdges.push({
          id: `mapping-${String(mappingIndex).padStart(3, '0')}`,
          sourcePath: finalSourcePath,
          targetPath,
          label: `${finalSourcePath} -> ${targetPath}`,
          enabled: true,
          transformation: {
            type: edge.data?.transformationType || "none",
            options: edge.data?.options || {},
            description: edge.data?.transformationType === "none" ? "Direct mapping" : `${edge.data?.transformationType} transformation`
          }
        });
      } else {
        // Multiple targets - split mapping
        const targetPaths = [];
        const splitMap = {};
        
        edges.forEach((edge, index) => {
          const targetNode = nodes.find(node => node.id === edge.target);
          const targetPath = targetNode ? buildTargetPath(targetNode, isErpToEcomm ? 'client' : 'customer') : edge.target;
          targetPaths.push(targetPath);
          splitMap[index.toString()] = targetPath;
        });

        // If direction is eCommerce → ERP, ensure sourcePath has [] for array parents
        const firstEdge = edges[0];
        const firstSourceNode = nodes.find(node => node.id === firstEdge?.source);
        const finalSourcePath = isErpToEcomm ? sourcePath : (firstSourceNode ? buildSourcePath(firstSourceNode, 'customer') : sourcePath);

        formattedEdges.push({
          id: `mapping-${String(mappingIndex).padStart(3, '0')}`,
          sourcePath: finalSourcePath,
          targetPath: "", // Empty for split mappings
          label: `${finalSourcePath} -> ${targetPaths.join(', ')}`,
          enabled: true,
          transformation: {
            type: "split",
            options: {
              delimiter: " ",
              array: splitMap
            },
            description: `Splits ${finalSourcePath} into ${targetPaths.length} parts and maps them to respective fields`
          }
        });
      }
      mappingIndex++;
    });

    // ── Virtual entity save ──────────────────────────────────────────────────
    // Saves canvas edges to localStorage AND persists the mapping pairs
    // to the backend API (shipping-mappings / payment-mappings / salesPerson-mappings).
    if (selectedEntity?.isVirtual) {
      const entityName = selectedEntity.entityName?.toLowerCase();
      const dirKey = isErpToEcomm ? 'erp_to_ecom' : 'ecom_to_erp';
      const entityKey = `virtualEdges_${entityName}_${dirKey}`;
      try {
        const edgesToPersist = fieldEdges.filter(e => e.type === 'custom' && !e.id.includes('tree-edge'));
        localStorage.setItem(entityKey, JSON.stringify(edgesToPersist));
      } catch (_) {}

      // Persist the pairs on the canvas to the backend API: the auto edges for saved/added
      // pairs plus any edge the user connected by hand between two value nodes.
      const virtualPairs = getVirtualPairsFromEdges(fieldEdges, virtualGraph.nodes);
      // Once saved, the returned list is redrawn as auto edges, so every edge on the value nodes is dropped first
      const virtualNodeIds = new Set(virtualGraph.nodes.map(node => node.id));
      const clearVirtualEdges = () => setFieldEdges(prev =>
        prev.filter(e => !virtualNodeIds.has(e.source) && !virtualNodeIds.has(e.target)));
      let apiPromise = null;
      if (entityName === 'shipping' && connectorId && instanceId) {
        const body = toMethodMappingsBody(virtualPairs, shippingMappings);
        apiPromise = saveShippingMappings(connectorId, instanceId, body)
          .then(res => {
            // Sync state to reflect deleted pairs
            const fresh = Array.isArray(res.data) ? res.data : body;
            clearVirtualEdges();
            setShippingMappings(fresh);
            setSavedVirtualKeys(toVirtualPairKeys(VIRTUAL_CONFIG.shipping, fresh));
            setRemovedShippingIndices(new Set());
          });
      } else if (entityName === 'payment' && connectorId && instanceId) {
        const body = toMethodMappingsBody(virtualPairs, paymentMappings);
        apiPromise = savePaymentMappings(connectorId, instanceId, body)
          .then(res => {
            // Sync state to reflect deleted pairs
            const fresh = Array.isArray(res.data) ? res.data : body;
            clearVirtualEdges();
            setPaymentMappings(fresh);
            setSavedVirtualKeys(toVirtualPairKeys(VIRTUAL_CONFIG.payment, fresh));
            setRemovedPaymentIndices(new Set());
          });
      } else if (entityName === 'sales person' && connectorId) {
        // An ERP sales person maps to a single eCommerce one (same rule as the Sales Person tab)
        const erpCodes = virtualPairs.map(pair => pair.erp);
        const repeatedCode = erpCodes.find((code, index) => erpCodes.indexOf(code) !== index);
        if (repeatedCode !== undefined) {
          setIsSaving(false);
          setMessage({
            type: 'error',
            title: 'Error',
            content: `ERP sales person "${repeatedCode}" is connected more than once. Keep one mapping per ERP sales person.`,
          });
          return false;
        }
        const body = toSalesPersonMappingsBody(virtualPairs, salesPersonMappings, salesPersonOptions);
        apiPromise = saveSalesPersonMappings(connectorId, body)
          .then(res => {
            // This endpoint answers { result, message, id } instead of the saved list
            if (!res.data?.result) {
              throw new Error(res.data?.message || 'Failed to save sales person mappings');
            }
            clearVirtualEdges();
            setSalesPersonMappings(body);
            setSavedVirtualKeys(toVirtualPairKeys(VIRTUAL_CONFIG['sales person'], body));
            setRemovedSalesPersonIndices(new Set());
          });
      }

      if (apiPromise) {
        return apiPromise
          .then(() => {
            // Remove localStorage cache — data is now persisted in the backend
            try { localStorage.removeItem(entityKey); } catch (_) {}
            setIsSaving(false);
            setMessage('Mapping saved successfully!');
            setHasUnsavedChanges(false);
            markEdgesSaved();
            return true;
          })
          .catch((err) => {
            console.error('Failed to save virtual mappings:', err);
            setIsSaving(false);
            setMessage({ type: 'error', title: 'Error', content: 'Failed to save mapping' });
            return false;
          });
      }
      // custom attributes or no API needed — clear localStorage too
      try { localStorage.removeItem(entityKey); } catch (_) {}
      setIsSaving(false);
      setMessage('Mapping saved successfully!');
      setHasUnsavedChanges(false);
      markEdgesSaved();
      return true;
    }
    // ── Non-virtual entity save ──────────────────────────────────────────────

    const bodyNew = {
      "id": selectedEntity?.entityName,
      "name": `${selectedEntity?.entityName} Data`,
      "description": `Maps ${selectedEntity?.entityName} data between ${labels.customerLabel} and ${labels.clientLabel}`,
      enabled: true,
      fieldMappings: formattedEdges,
    }

    const body = {
      type: isErpToEcomm ? "Source" : "Target",
      "subscriptionId": subscriptionid,
      "mappingSchema": JSON.stringify(bodyNew),
      "entityName": selectedEntity?.entityName?.toLowerCase().replace(/\s+/g, '')
    }

    const savePromise = saveMapping(body)
      .then(() => {
        setIsSaving(false); // Stop spinner
        setMessage('Mapping saved successfully!'); // Set success message
        setHasUnsavedChanges(false); // Reset unsaved changes flag
        markEdgesSaved();
        return true;
      }).catch(() => {
        setIsSaving(false); // Stop spinner
        setMessage({
          type: 'error',
          title: 'Error',
          content: 'Failed to save mapping'
        });
        return false;
      });

    localStorage.setItem('graphState', JSON.stringify(state));
    return savePromise;
  }, [markEdgesSaved, connectedMappings, fieldEdges, isErpToEcomm, labels, nodes, selectedEntity, setIsSaving, setMessage, setHasUnsavedChanges, subscriptionid, shippingMappings, paymentMappings, salesPersonMappings, salesPersonOptions, virtualGraph, setFieldEdges, connectorId, instanceId]);
  
  // Function to generate AI mappings between nodes
  const generateAIMappings = useCallback(() => {
    // Get all leaf nodes from both sides
    const leftNodes = nodes.filter(node => 
      node.data.parentId === 'customer' && !node.data.hasChildren
    );
    
    const rightNodes = nodes.filter(node => 
      node.data.parentId === 'client' && !node.data.hasChildren
    );
    
    // If either side has no nodes, we can't create mappings
    if (leftNodes.length === 0 || rightNodes.length === 0) {
      return [];
    }
    
    // Array to store generated mappings
    const generatedMappings = [];
    
    // Simple name-based matching algorithm
    // This is a basic implementation - in a real app, you'd use more sophisticated NLP
    leftNodes.forEach(leftNode => {
      const leftLabel = leftNode.data.label.toLowerCase();
      
      // Find potential matches on the right side
      const potentialMatches = rightNodes.map(rightNode => {
        const rightLabel = rightNode.data.label.toLowerCase();
        
        // Calculate a simple similarity score
        let score = 0;
        
        // Exact match gets highest score
        if (leftLabel === rightLabel) {
          score = 1.0;
        } 
        // Contains relationship
        else if (leftLabel.includes(rightLabel) || rightLabel.includes(leftLabel)) {
          score = 0.8;
        }
        // Word similarity (check if words from one label appear in the other)
        else {
          const leftWords = leftLabel.split(/\W+/);
          const rightWords = rightLabel.split(/\W+/);
          
          // Count matching words
          const matchingWords = leftWords.filter(word => 
            word.length > 2 && rightWords.includes(word)
          ).length;
          
          if (matchingWords > 0) {
            score = 0.5 + (0.3 * matchingWords / Math.max(leftWords.length, rightWords.length));
          }
          
          // Check for common field patterns including dot notation
          const commonPatterns = [
            { left: ['id', 'no', 'number', 'code', 'product.id'], right: ['id', 'sku', 'code', 'product.id'] },
            { left: ['name', 'title', 'label', 'product.name'], right: ['name', 'title', 'label', 'product.name'] },
            { left: ['desc', 'description', 'text'], right: ['desc', 'description', 'text'] },
            { left: ['price', 'cost', 'amount'], right: ['price', 'cost', 'amount'] },
            { left: ['date', 'time'], right: ['date', 'time'] },
            { left: ['qty', 'quantity', 'count'], right: ['qty', 'quantity', 'count'] },
            { left: ['sku', 'product.sku'], right: ['sku', 'product.sku'] },
            { left: ['attribute_set_id', 'product.attribute_set_id'], right: ['attribute_set_id', 'product.attribute_set_id'] }
          ];
          
          for (const pattern of commonPatterns) {
            const leftMatch = pattern.left.some(term => leftLabel.includes(term));
            const rightMatch = pattern.right.some(term => rightLabel.includes(term));
            
            if (leftMatch && rightMatch) {
              score = Math.max(score, 0.7);
              break;
            }
          }
        }
        
        return {
          node: rightNode,
          score
        };
      });
      
      // Sort by score and get the best match
      const bestMatches = potentialMatches
        .filter(match => match.score > 0.5) // Only consider good matches
        .sort((a, b) => b.score - a.score);
      
      // Take the best match if available
      if (bestMatches.length > 0) {
        const bestMatch = bestMatches[0];
        
        // Check if this right node is already mapped with a better score
        const existingMapping = generatedMappings.find(
          m => m.target === bestMatch.node.id
        );
        
        if (!existingMapping || existingMapping.score < bestMatch.score) {
          // If there's an existing mapping with a lower score, remove it
          if (existingMapping) {
            const index = generatedMappings.findIndex(m => m.target === bestMatch.node.id);
            if (index !== -1) {
              generatedMappings.splice(index, 1);
            }
          }
          
          // Add the new mapping
          generatedMappings.push({
            source: leftNode.id,
            target: bestMatch.node.id,
            score: bestMatch.score,
            sourceLabel: leftNode.data.label,
            targetLabel: bestMatch.node.data.label
          });
        }
      }
    });
    
    // Create edges for the generated mappings
    const createdMappings = [];
    
    generatedMappings.forEach(mapping => {
      // Generate a unique ID for the edge
      const edgeId = `ai-mapping-${mapping.source}-${mapping.target}`;
      
      // Check if this edge already exists
      const edgeExists = fieldEdges.some(
        edge => edge.source === mapping.source && edge.target === mapping.target
      );
      
      if (!edgeExists) {
        // Create the edge parameters
        const params = {
          id: edgeId,
          source: mapping.source,
          target: mapping.target,
          sourceHandle: 'r',
          targetHandle: 'l',
          type: 'custom',
          data: {
            label: `${mapping.sourceLabel} → ${mapping.targetLabel}`,
            transformationType: 'none',
            hasTransformation: false,
            aiGenerated: true,
            confidence: mapping.score
          }
        };
        
        // Add the edge
        handleConnect(params);
        createdMappings.push(mapping);
      }
    });
    
    return createdMappings;
  }, [nodes, fieldEdges, handleConnect]);

  // Track previous entityId to avoid duplicate API calls
  const prevEntityIdRef = useRef();

  // Load schema and saved mappings whenever the selected entity changes
  useEffect(() => {
    if (selectedEntity && selectedEntity.entityId) {
      // Virtual entities (Payment, Shipping, etc.) have no backend schema.
      // Render a blank canvas immediately without making any API calls.
      if (selectedEntity.isVirtual) {
        setSchemaError(null);
        setIsLoadingSchema(false);
        setInitialLoadComplete(false); // will be set true by the dedicated useEffect below
        setHasUnsavedChanges(false);
        setErpMethods([]);
        setEcommerceMethods([]);
        setSavedMappings([]);
        // Nodes and edges for virtual entities come from virtualGraph and its
        // auto-edge useEffect — do NOT override them here.
        // Clear mappings for entities we're NOT currently on.
        // The dedicated useEffects (watching selectedEntity + connectorId + instanceId)
        // are responsible for fetching the correct ones when active.
        if (selectedEntity.entityName?.toLowerCase() !== 'shipping') {
          setShippingMappings([]);
        }
        if (selectedEntity.entityName?.toLowerCase() !== 'payment') {
          setPaymentMappings([]);
        }
        if (selectedEntity.entityName?.toLowerCase() !== 'sales person') {
          setSalesPersonMappings([]);
        }
        setRemovedShippingIndices(new Set());
        setRemovedPaymentIndices(new Set());
        setRemovedSalesPersonIndices(new Set());
        return;
      }

      if (subscriptionid) {
        setIsLoadingSchema(true); // Start loading
        setInitialLoadComplete(false); // reset per-entity dirty tracker
        setHasUnsavedChanges(false);
        setSchemaError(null); // Clear any previous error
        setShippingMappings([]);  // non-virtual entity — clear virtual-only mappings
        setPaymentMappings([]);
        setSalesPersonMappings([]);
        setRemovedShippingIndices(new Set());
        setRemovedPaymentIndices(new Set());
        setRemovedSalesPersonIndices(new Set());
        getSubscription(subscriptionid)
        .then(response => {
          const endpoints = response.data.endpoints;

          if (Array.isArray(endpoints)) {
            let pendingRequests = endpoints.length; // 1 request per endpoint (the schema flow)
            
            const checkAndSetLoadingComplete = () => {
              pendingRequests--;
              if (pendingRequests <= 0) {
                setIsLoadingSchema(false);
              }
            };
            
            endpoints.forEach(endpoint => {
              const schemaBody = {
                endpointId: endpoint.id,
                subscriptionId: subscriptionid,
                entityName: selectedEntity?.entityName?.toLowerCase().replace(/\s+/g, ''),
                isForceRefresh: false
              };
              getSchema(schemaBody)
                .then(response => {
                  // Check if we have valid schema data
                  if (!response.data || !response.data.properties) {
                    console.warn(`No schema properties found for endpointId ${endpoint.id}`);
                    setSchemaError(`Schema not ready yet for selected component: ${selectedEntity?.entityName || selectedEntity?.tabName || selectedEntity?.name || 'component'}`);
                    checkAndSetLoadingComplete();
                    return;
                  }
                  getMarketplaceEndpoints()
                    .then(endpointResponse => {
                      // Find the endpoint from endpointResponse.data where id matches endpoint.id
                      if (Array.isArray(endpointResponse.data)) {
                        const matched = endpointResponse.data.find(e => e.id === endpoint.id);
                        // If type is 'Erp', setErpMethods from response.data
                        if (matched && matched.type === 'Erp') {
                          setErpMethods(response.data.properties);
                          // Do not override labels here; they are derived from workflow/business context
                          // Keeping existing labels intact
                        }
                        // If type is 'Ecommerce', setEcommerceMethods from response.data
                        if (matched && matched.type === 'Ecommerce') {
                          setEcommerceMethods(response.data.properties);
                          // Do not override labels here; they are derived from workflow/business context
                          // Keeping existing labels intact
                        }
                        const mapBody = {
                          endpointId: endpoint.id,
                          subscriptionId: subscriptionid,
                          entityName: selectedEntity?.entityName?.toLowerCase().replace(/\s+/g, ''),
                        }

                        getMapping(mapBody)
                        .then(mappingResponse => {
                          // Choose schema based on toggle direction
                          const schemaToUse = isErpToEcomm ? 'sourceMappingSchema' : 'targetMappingSchema';

                          // Normalize and extract fieldMappings regardless of shape
                          let candidate = mappingResponse.data?.[schemaToUse]
                            ?? mappingResponse.data?.mappingSchema
                            ?? mappingResponse.data;
                          try {
                            candidate = typeof candidate === 'string' ? JSON.parse(candidate) : candidate;
                          } catch (e) {
                            console.error('Failed to parse mapping payload:', e, { data: mappingResponse.data });
                          }
                          const fieldMappings = Array.isArray(candidate?.fieldMappings)
                            ? candidate.fieldMappings
                            : Array.isArray(candidate)
                              ? candidate
                              : [];
                          setSavedMappings(fieldMappings);

                          // Build nodes and edges from fieldMappings (supports split transformations)
                          if (Array.isArray(fieldMappings) && fieldMappings.length > 0) {
                            setNodes(prevNodes => {
                              let updatedNodes = [...prevNodes];
                              // Ensure root nodes exist for traversal
                              const ensureRoot = (id, label) => {
                                let node = updatedNodes.find(n => n.id === id);
                                if (!node) {
                                  node = {
                                    id,
                                    data: { label, parentId: null, isCollapsed: false },
                                    position: { x: id === 'customer' ? -900 : 900, y: 200 }
                                  };
                                  updatedNodes.push(node);
                                }
                              };
                              ensureRoot('customer', 'Customer');
                              ensureRoot('client', 'Client');
                              const findOrAddNode = (label, parentId, side) => {
                                const cleanLabel = (label || 'unknown').replace(/\[\]/g, '');
                                let node = updatedNodes.find(n => n.data.label === cleanLabel && n.data.parentId === parentId);
                                if (!node) {
                                  const newNodeId = `${side}-mapping-${cleanLabel}`;
                                  node = {
                                    id: newNodeId,
                                    data: {
                                      label: cleanLabel,
                                      parentId,
                                      isCollapsed: false
                                    },
                                    position: { x: side === 'left' ? 100 : 700, y: 200 + Math.random() * 200 }
                                  };
                                  updatedNodes.push(node);
                                }
                                return node.id;
                              };

                              // Helper function to create nested nodes from a path
                              const findOrAddNestedNode = (path, parentId, side) => {
                                if (!path) return null;
                                
                                // If the path contains dots, it's a nested path
                                if (path.includes('.')) {
                                  const pathParts = path.split('.');
                                  let currentParentId = parentId;
                                  let lastNodeId = null;
                                  
                                  // Create each part of the path as a node
                                  for (let i = 0; i < pathParts.length; i++) {
                                    const partRaw = pathParts[i];
                                    const part = partRaw.replace(/\[\]/g, ''); // remove [] from array notation
                                    // Check if this node already exists
                                    const lookupParentId = currentParentId;
                                    let existingNode = updatedNodes.find(n => 
                                      n.data.label === part && n.data.parentId === lookupParentId
                                    );
                                    
                                    if (!existingNode) {
                                      // Create a new node for this part
                                      const newNodeId = `${side}-${part}-${i}`;
                                      existingNode = {
                                        id: newNodeId,
                                        data: {
                                          label: part,
                                          parentId: currentParentId,
                                          isCollapsed: false,
                                          fullPath: pathParts
                                            .slice(0, i + 1)
                                            .map(p => p.replace(/\[\]/g, ''))
                                            .join('.')
                                        },
                                        position: { x: side === 'left' ? 100 : 700, y: 200 + Math.random() * 200 }
                                      };
                                      updatedNodes.push(existingNode);
                                    }
                                    
                                    // Update parent for next iteration
                                    currentParentId = existingNode.id;
                                    lastNodeId = existingNode.id;
                                  }
                                  
                                  return lastNodeId;
                                } else {
                                  // For simple paths, use the original function
                                  return findOrAddNode(path.replace(/\[\]/g, ''), parentId, side);
                                }
                              };
                              
                              fieldMappings.forEach(mapping => {
                                if (!mapping?.sourcePath) return;
                                
                                // Source under customer - handle nested paths
                                findOrAddNestedNode(mapping.sourcePath, 'customer', 'left');

                                // Targets: handle split or single
                                const isSplit = mapping?.transformation?.type === 'split';
                                const splitTargets = isSplit && mapping?.transformation?.options?.array
                                  ? Object.values(mapping.transformation.options.array)
                                  : null;

                                if (Array.isArray(splitTargets) && splitTargets.length) {
                                  splitTargets.forEach(t => findOrAddNestedNode(String(t), 'client', 'right'));
                                } else if (mapping?.targetPath) {
                                  findOrAddNestedNode(mapping.targetPath, 'client', 'right');
                                }
                              });

                              // Build edges immediately using the same updatedNodes to avoid stale state issues
                              const localFindLeafNodeId = (path, rootParentId) => {
                                if (!path) return null;
                                const cleanPath = String(path).replace(/\[\]/g, '');
                                if (cleanPath.includes('.')) {
                                  const parts = cleanPath.split('.');
                                  let parent = rootParentId;
                                  let current = null;
                                  for (let i = 0; i < parts.length; i++) {
                                    const part = parts[i];
                                    const lookupParent = parent;
                                    current = updatedNodes.find(n => n.data.label === part && n.data.parentId === lookupParent);
                                    if (!current) return null;
                                    parent = current.id;
                                  }
                                  return current?.id || null;
                                } else {
                                  const node = updatedNodes.find(n => n.data.label === cleanPath && n.data.parentId === rootParentId);
                                  return node?.id || null;
                                }
                              };

                              const newEdges = [];
                              fieldMappings.forEach(mapping => {
                                if (!mapping?.sourcePath) return;
                                const sourceId = localFindLeafNodeId(mapping.sourcePath, 'customer') ||
                                  `left-${String(mapping.sourcePath).replace(/\[\]/g, '').replace(/\./g, '-')}`;

                                const isSplit = mapping?.transformation?.type === 'split';
                                const splitTargets = isSplit && mapping?.transformation?.options?.array
                                  ? Object.values(mapping.transformation.options.array)
                                  : null;

                                const addLocalEdge = (targetPath, idx) => {
                                  const targetId = localFindLeafNodeId(targetPath, 'client') ||
                                    `right-${String(targetPath).replace(/\[\]/g, '').replace(/\./g, '-')}`;
                                  const edgeId = mapping.id ? `${mapping.id}${isSplit ? `-split-${idx}` : ''}`
                                    : `mapping-edge-${sourceId}-${targetId}`;
                                  if (!newEdges.some(e => e.id === edgeId)) {
                                    newEdges.push({
                                      id: edgeId,
                                      source: sourceId,
                                      target: targetId,
                                      sourceHandle: 'r',
                                      targetHandle: 'l',
                                      type: 'custom',
                                      style: { ...DEFAULT_EDGE_STYLE },
                                      data: {
                                        label: mapping.label || `${mapping.sourcePath} → ${targetPath}`,
                                        transformationType: mapping.transformation?.type || 'none',
                                        hasTransformation: mapping.transformation?.type !== 'none',
                                        options: mapping.transformation?.options || {},
                                        description: mapping.transformation?.description || ''
                                      }
                                    });
                                  }
                                };

                                if (Array.isArray(splitTargets) && splitTargets.length) {
                                  splitTargets.forEach((t, i) => addLocalEdge(String(t), i));
                                } else if (mapping?.targetPath) {
                                  addLocalEdge(mapping.targetPath, 0);
                                }
                              });
                              setFieldEdges(newEdges);

                              return autoPositionNodes(inferNodeTypesMultilevel(updatedNodes));
                            });

                            // Edges already set from the same node build pass above to avoid race conditions
                            setFieldEdges(prev => prev);
                          }

                          // Parse mappingSchema if present and is a string
                          let mappingSchemaObj = null;
                          if (
                            mappingResponse.data &&
                            typeof mappingResponse.data.mappingSchema === 'string'
                          ) {
                            try {
                              mappingSchemaObj = JSON.parse(mappingResponse.data.mappingSchema);

                              // --- Begin: Show default connections from mappingSchemaObj ---
                              if (mappingSchemaObj) {
                                // Use mappingSchemaObj.fieldMappings if present, otherwise use mappingSchemaObj directly if it's an array
                                const mappings = Array.isArray(mappingSchemaObj.fieldMappings)
                                  ? mappingSchemaObj.fieldMappings
                                  : Array.isArray(mappingSchemaObj)
                                    ? mappingSchemaObj
                                    : [];

                                // Add nodes and edges for each mapping
                                setNodes(prevNodes => {
                                  let updatedNodes = [...prevNodes];
                                  // Helper to find or add a node
                                  const findOrAddNode = (label, parentId, side) => {
                                    let node = updatedNodes.find(n => n.data.label === label && n.data.parentId === parentId);
                                    if (!node) {
                                      const newNodeId = `${side}-mapping-${label}`;
                                      node = {
                                        id: newNodeId,
                                        data: {
                                          label,
                                          parentId,
                                          isCollapsed: false
                                        },
                                        position: { x: side === 'left' ? 100 : 700, y: 200 + Math.random() * 200 }
                                      };
                                      updatedNodes.push(node);
                                    }
                                    return node.id;
                                  };

                                  mappings.forEach(mapping => {
                                    // Add source node under customer
                                    findOrAddNode(mapping.sourcePath, 'customer', 'left');
                                    // Add target node under client
                                    findOrAddNode(mapping.targetPath, 'client', 'right');
                                  });
                                  // Re-run type inference and positioning
                                  return autoPositionNodes(inferNodeTypesMultilevel(updatedNodes));
                                });

                                setFieldEdges(prevEdges => {
                                  let updatedEdges = [...prevEdges];
                                  mappings.forEach(mapping => {
                                    const sourceNodeId = `left-mapping-${mapping.sourcePath}`;
                                    const targetNodeId = `right-mapping-${mapping.targetPath}`;
                                    // Avoid duplicate edges
                                    if (!updatedEdges.some(e => e.source === sourceNodeId && e.target === targetNodeId)) {
                                      updatedEdges.push({
                                        id: mapping.id || `mapping-edge-${sourceNodeId}-${targetNodeId}`,
                                        source: sourceNodeId,
                                        target: targetNodeId,
                                        sourceHandle: 'r',
                                        targetHandle: 'l',
                                        type: 'custom',
                                        style: { ...DEFAULT_EDGE_STYLE },
                                        data: {
                                          label: mapping.label || `${mapping.sourcePath} → ${mapping.targetPath}`,
                                          transformationType: mapping.transformation?.type || 'none',
                                          hasTransformation: mapping.transformation?.type !== 'none',
                                          options: mapping.transformation?.options || {},
                                          description: mapping.transformation?.description || ''
                                        }
                                      });
                                    }
                                  });
                                  return updatedEdges;
                                });
                              }
                              // --- End: Show default connections from mappingSchemaObj ---

                            } catch (e) {
                              console.error('Failed to parse mappingSchema:', e);
                            }
                          }
                          // ...you can now use mappingSchemaObj as a JS object...
                          checkAndSetLoadingComplete();
                        }).catch(err => {
                          console.error(`Error fetching GetMapping for endpointId ${endpoint.id}:`, err);
                          // Only show error for actual API failures (4xx, 5xx status codes)
                          if (err.response && err.response.status >= 400) {
                            setSchemaError(`Schema not ready yet for selected component: ${selectedEntity?.entityName || selectedEntity?.tabName || selectedEntity?.name || 'component'}`);
                          }
                          checkAndSetLoadingComplete();
                        });
                      } else {
                        checkAndSetLoadingComplete();
                      }
                    })
                    .catch(err => {
                      console.error(`Error fetching endpoints for endpointId ${endpoint.id}:`, err);
                      // Only show error for actual API failures (4xx, 5xx status codes)
                      if (err.response && err.response.status >= 400) {
                        setSchemaError(`Schema not ready yet for selected component: ${selectedEntity?.entityName || selectedEntity?.tabName || selectedEntity?.name || 'component'}`);
                      }
                      checkAndSetLoadingComplete();
                    });
                })
                .catch(err => {
                  console.error(`Error fetching Schema for endpointId ${endpoint.id}:`, err);
                  // Only show error for actual API failures (4xx, 5xx status codes)
                  if (err.response && err.response.status >= 400) {
                    setSchemaError(`Schema not ready yet for selected component: ${selectedEntity?.entityName || selectedEntity?.tabName || selectedEntity?.name || 'component'}`);
                  }
                  checkAndSetLoadingComplete();
                });
            })
          } else {
            // No endpoints found, complete loading immediately
            setIsLoadingSchema(false);
          }
        })
        .catch(err => {
          console.error('Error fetching subscription details:', err);
          setIsLoadingSchema(false);
          // Only show error for actual API failures (4xx, 5xx status codes)
          if (err.response && err.response.status >= 400) {
            setSchemaError(`Schema not ready yet for selected component: ${selectedEntity?.entityName || selectedEntity?.tabName || selectedEntity?.name || 'component'}`);
          }
        });
      }

      if (prevEntityIdRef.current !== selectedEntity.entityId) {
        prevEntityIdRef.current = selectedEntity.entityId;
      }
    }
  }, [isErpToEcomm, selectedEntity, setEcommerceMethods, setErpMethods, setFieldEdges, setIsLoadingSchema, setNodes, setSavedMappings, setSchemaError, subscriptionid]);
  
  // Utility to build mapping edges from nodes and savedMappings
  const buildFieldEdgesFromSavedMappings = useCallback((nodeList, savedMappingsList) => {
    if (!Array.isArray(savedMappingsList) || savedMappingsList.length === 0) return [];
    const edges = [];

    const normalizePath = (p) => (typeof p === 'string' ? p.replace(/\[\]/g, '') : p);

    const findNodeByPath = (rootParentId, path) => {
      if (!path) return null;
      const clean = normalizePath(String(path));
      const direct = nodeList.find(
        (n) => n.data.fullPath && normalizePath(n.data.fullPath) === clean && n.data.parentId !== null
      );
      if (direct) return direct;

      const parts = clean.split('.');
      let parent = nodeList.find((n) => n.data.label === parts[0] && n.data.parentId === rootParentId);
      for (let i = 1; i < parts.length && parent; i++) {
        const lookupParentId = parent.id;
        parent = nodeList.find((n) => n.data.label === parts[i] && n.data.parentId === lookupParentId);
      }
      return parent || null;
    };

    savedMappingsList.forEach((mapping) => {
      const srcPath = normalizePath(mapping.sourcePath);
      const tgtPath = mapping.targetPath ? normalizePath(mapping.targetPath) : null;

      let sourceNode = findNodeByPath('customer', srcPath);
      if (!sourceNode) {
        sourceNode = nodeList.find((n) => n.data.label === srcPath && n.data.parentId === 'customer');
      }

      if (mapping.transformation?.type === 'split' && mapping.transformation?.options?.array) {
        Object.entries(mapping.transformation.options.array).forEach(([splitIndex, rawTargetPath]) => {
          const targetPath = normalizePath(String(rawTargetPath));
          const targetNode = findNodeByPath('client', targetPath) ||
            nodeList.find((n) => n.data.label === targetPath && n.data.parentId === 'client');

          if (sourceNode && targetNode) {
            edges.push({
              id: `${mapping.id}-split-${splitIndex}`,
              source: sourceNode.id,
              target: targetNode.id,
              sourceHandle: 'r',
              targetHandle: 'l',
              type: 'custom',
              style: { ...DEFAULT_EDGE_STYLE },
              data: {
                label: `${mapping.sourcePath} → ${rawTargetPath}`,
                transformationType: 'split',
                hasTransformation: true,
                options: mapping.transformation.options,
                description: mapping.transformation.description || '',
                splitIndex: splitIndex,
                originalMappingId: mapping.id
              }
            });
          }
        });
      } else {
        const targetNode = tgtPath
          ? findNodeByPath('client', tgtPath) || nodeList.find((n) => n.data.label === tgtPath && n.data.parentId === 'client')
          : null;

        if (sourceNode && targetNode) {
          edges.push({
            id: mapping.id || `mapping-edge-${sourceNode.id}-${targetNode.id}`,
            source: sourceNode.id,
            target: targetNode.id,
            sourceHandle: 'r',
            targetHandle: 'l',
            type: 'custom',
            style: { ...DEFAULT_EDGE_STYLE },
            data: {
              label: mapping.label || `${mapping.sourcePath} → ${mapping.targetPath}`,
              transformationType: mapping.transformation?.type || 'none',
              hasTransformation: mapping.transformation?.type !== 'none',
              options: mapping.transformation?.options || {},
              description: mapping.transformation?.description || ''
            }
          });
        }
      }
    });
    return edges;
  }, []);

  // After nodes and savedMappings are loaded, build fieldEdges from them
  useEffect(() => {
    if (nodes.length > 0 && Array.isArray(savedMappings) && savedMappings.length > 0) {
      const builtEdges = buildFieldEdgesFromSavedMappings(nodes, savedMappings);
      setFieldEdges(prevEdges => {
        // Remove previous mapping edges (with type 'custom') and add new ones
        const nonMappingEdges = prevEdges.filter(e => e.type !== 'custom');
        return [...nonMappingEdges, ...builtEdges];
      });
    }
    // Optionally, you may want to only run this when savedMappings changes, or when nodes are stable
  }, [buildFieldEdgesFromSavedMappings, nodes, savedMappings, setFieldEdges]);

  // Function to validate and format target paths with array notation
  const validateTargetPath = useCallback((targetPath, defaultArrayIndex = 0) => {
    if (!targetPath || typeof targetPath !== 'string') return targetPath;
    
    const pathParts = targetPath.split('.');
    let formattedPath = '';
    
    for (let i = 0; i < pathParts.length; i++) {
      const currentPart = pathParts[i];
      
      if (i === 0) {
        formattedPath = currentPart;
        continue;
      }
      
      // Find the parent node for this part
      const parentParts = pathParts.slice(0, i);
      const parentPath = parentParts.join('.');
      
      // Try to find the node that corresponds to the parent
      const parentNode = nodes.find(node => {
        const nodePath = node.data.fullPath || node.data.label;
        return nodePath === parentParts[parentParts.length - 1] || 
               nodePath === parentPath ||
               node.data.label === parentParts[parentParts.length - 1];
      });
      
      // Check if parent node is an array type
      const isParentArray = parentNode && (
        parentNode.data.fieldType === 'array' || 
        parentNode.data.fieldType === 'object[]' ||
        (parentNode.data.fieldType && parentNode.data.fieldType.toLowerCase().includes('array'))
      );
      
      if (isParentArray) {
        // Add index number (use node-specific index or default)
        const arrayIndex = parentNode.data.arrayIndex !== undefined ? parentNode.data.arrayIndex : defaultArrayIndex;
        formattedPath += `[${arrayIndex}].${currentPart}`;
      } else {
        formattedPath += `.${currentPart}`;
      }
    }
    
    return formattedPath;
  }, [nodes]);

  // Function to process formatted edges array with path validation
  const processFormattedEdges = useCallback((formattedEdges, defaultArrayIndex = 0) => {
    if (!Array.isArray(formattedEdges)) return formattedEdges;
    
    return formattedEdges.map(edge => {
      const originalTargetPath = edge.targetPath;
      const validatedTargetPath = validateTargetPath(originalTargetPath, defaultArrayIndex);
      
      return {
        ...edge,
        targetPath: validatedTargetPath,
        label: `${edge.sourcePath} -> ${validatedTargetPath}`
      };
    });
  }, [validateTargetPath]);

  // Tell the page whether leaving would lose work: a drawn edge not saved yet, or a saved one removed/edited
  const isDirty = hasEditedSavedEdges || fieldEdges.some(edge => edge.data?.unsaved);
  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);
  useEffect(() => () => setDirty(false), [setDirty]);
  useEffect(() => {
    registerSave(saveState);
    return () => registerSave(null);
  }, [saveState, registerSave]);

  const contextValue = {
    nodes,
    edges: allEdges,
    onNodesChange,
    onEdgesChange,
    handleConnect,
    updateEdgeLabel,
    setEdgeTransformation,
    selectNode,
    removeEdge,
    applySuggestion,
    applyMultipleSuggestions,
    saveState,
    handleNodeCollapse,
    isValidConnection,
    suggestChildMappings,
    addNode,
    generateAIMappings,
    validateTargetPath,
    processFormattedEdges,
    erpMethods,
    ecommerceMethods,
    isSaving,
    isLoadingSchema,
    schemaError,
    setSchemaError,
    message,
    setMessage,
    isErpToEcomm,
    toggleDirection,
    labels,
    originalSystemNames,
    isSkeletonLoading,
    showSkeletonTree,
    hideSkeletonTree,
    hasUnsavedChanges,
    customAttributes,
    addCustomAttribute,
    shippingMappings,
    setShippingMappings,
    paymentMappings,
    setPaymentMappings,
    salesPersonMappings,
    setSalesPersonMappings,
    shippingOptions,
    paymentOptions,
    salesPersonOptions,
    removedShippingIndices,
    removedPaymentIndices,
    removedSalesPersonIndices,
    selectedEntity,
    connectorId,
    instanceId,
  };
  
  return (
    <GraphContext.Provider value={contextValue}>
      {children}
      {/* Spinner overlay, show only when saving */}
      {isSaving && (
        <div style={{
          position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh',
          background: 'rgba(255,255,255,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className={Style.spinner} />
        </div>
      )}

      {/* Schema error message */}
      <Modal show={!!schemaError} onHide={() => setSchemaError(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className={Style.dialogTitle}>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body className={Style.dialogBody}>{schemaError}</Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setSchemaError(null)}>Close</button>
        </Modal.Footer>
      </Modal>
      <Modal show={Boolean(message && message.type === 'error')} onHide={() => setMessage(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className={Style.dialogTitle}>{message?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className={`${Style.dialogBody} ${Style.preLine}`}>{message?.content}</Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setMessage(null)}>Close</button>
        </Modal.Footer>
      </Modal>
      {/* Show SuccessPopup for other message types */}
      {message && (typeof message === 'string' || message.type !== 'error') && (
        <SuccessPopup
          message={typeof message === 'string' ? message : message.message}
          onClose={() => setMessage(null)}
        />
      )}
    </GraphContext.Provider>
  );
};

// Export hook to use the context
export const useGraphService = () => {
  const context = useContext(GraphContext);
  if (!context) {
    throw new Error('useGraphService must be used within a GraphProvider');
  }
  return context;
};